import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
    MessageCircle,
    CheckCircle2,
    Clock,
    Send,
    User,
    Tag,
    Calendar,
    Loader2,
    Eye,
    Download,
    X,
    File,
    Paperclip,
    Image as ImageIcon,
    Reply,
    ChevronDown,
    ChevronUp,
    Search,
    Volume2,
    Filter,
    Building2,
    Zap
} from 'lucide-react';
import VoiceRecorder from '../../components/Chat/VoiceRecorder';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import baseURL from '../../utils/baseURL';
import {
    connectSupportSocket,
    disconnectSupportSocket,
    getSupportSocket,
    isSupportSocketConnected,
    setOnReauthenticated,
    adminSocketEvents
} from '../../services/supportSocketService';
import { fetchSupportAgents } from '../../redux/supportQueueSlice';
import { getCategories, getAllSupportSubCategories } from '../../redux/masterSlice';
import { getTenantOptions } from '../../redux/tenantSlice';
import {
    FilterPopover, FilterPanel, FilterSection, FilterOptionList, FilterSearchableList,
} from '../../components/common/FilterPanel';
import getFileURL from '../../utils/fileURL';
import MasterLabel from '../../components/common/MasterLabel';
import AssigneeChip from './AssigneeChip';
import { formatTime as formatClockTime } from '../../utils/clock';

// Named once: this app's lint has no jsx-uses-vars, so `<motion.div>` alone
// reads as an unused import.
const MotionDiv = motion.div;
const MotionButton = motion.button;

// SUPER ADMIN SUPPORT QUEUE — a port of the tenant-admin inbox
// (Frontend-web/src/pages/support/AdminSupport.jsx), which the owner wanted
// kept as-is: same tabs, SLA rings, priority order, assign picker and chat.
//
// What changed, and why:
//   * `queue` prop. Every ticket now lands on this side, in one of two
//     queues: 'tenant' (hospital admins, doctors, staff, isolated-tenant
//     patients) and 'patient' (the Mitra One Care patient app). The server
//     joins this socket to `sa_support_${queue}` on requestAdminTickets and
//     every list/event is for that queue only.
//   * "All Open" tab — every open ticket of the queue, whoever holds it, so a
//     second agent can follow or take over. "My Tickets" is still what the
//     SERVER says is mine (adminTickets); nothing here decides that locally.
//   * No Escalate. There is no tier above this one to escalate to.
//   * No senderId / senderRole in any payload. The server takes the actor from
//     the verified token; a value typed by the client would be a lie waiting
//     to happen.
//   * Assign / reassign picker reads the agent pool for THIS queue over REST
//     (redux supportQueueSlice) — super admins plus the platform staff whose
//     role may respond here — instead of the tenant's admin list.

// A page-level (not per-instance) disconnect timer. React StrictMode mounts
// twice and the two support routes remount each other; both must be able to
// cancel a disconnect the previous instance scheduled, or the socket the new
// instance just connected would be torn down 500ms later.
let pendingDisconnect = null;

// Helper: find a master category/subcategory ID by name from the Redux-loaded lists.
// This is the safety net that makes translation work even when the backend API
// response doesn't include `categoryId` / `subCategoryId` on the ticket (e.g. for
// legacy closed tickets whose DB rows predate the projection fix).
const resolveSupportIds = (ticket, masterCategories, allSubCategories) => {
    let categoryId = ticket?.categoryId || null;
    let subCategoryId = ticket?.subCategoryId || null;
    if (categoryId && subCategoryId) return { categoryId, subCategoryId };

    const categoryNameGuess = ticket?.categoryName || ticket?.category || null;
    // `ticket.subject` is stored as the subcategory name snapshot at creation time,
    // so it's the most reliable name source when the explicit subCategory field is absent.
    const subCategoryNameGuess = ticket?.subCategoryName || ticket?.subCategory || ticket?.subject || null;

    if (!categoryId && categoryNameGuess && Array.isArray(masterCategories)) {
        const match = masterCategories.find(
            (c) => (c.name || '').toLowerCase() === `${categoryNameGuess}`.toLowerCase()
        );
        if (match) categoryId = match.categoryId || match.id;
    }
    if (!subCategoryId && subCategoryNameGuess && Array.isArray(allSubCategories)) {
        const match = allSubCategories.find(
            (s) => (s.name || '').toLowerCase() === `${subCategoryNameGuess}`.toLowerCase()
        );
        if (match) {
            subCategoryId = match.subCategoryId || match.id;
            if (!categoryId) categoryId = match.categoryId || null;
        }
    }
    return { categoryId, subCategoryId };
};

// Renders the ticket's category name with translation (parent category only —
// the subject line above already shows the subcategory snapshot).
const TicketCategoryLine = ({ ticket }) => {
    const masterCategories = useSelector((state) => state.master?.categories || []);
    const allSubCategories = useSelector((state) => state.master?.allSupportSubCategories || []);
    const { categoryId } = resolveSupportIds(ticket, masterCategories, allSubCategories);

    const hasCategory = ticket?.category || ticket?.categoryName || categoryId;
    if (!hasCategory) return null;

    return (
        <MasterLabel
            type="masterSupportCategory"
            id={categoryId}
            field="name"
            // ticket.category may hold the raw categoryId (normalizeTicket's last
            // fallback); an id is never drawn, so it only counts when it differs.
            fallback={ticket.categoryName || (ticket.category !== ticket.categoryId ? ticket.category : '') || ''}
        />
    );
};

// Translates the ticket `subject` field (the subcategory-name snapshot).
const TicketSubjectLabel = ({ ticket }) => {
    const masterCategories = useSelector((state) => state.master?.categories || []);
    const allSubCategories = useSelector((state) => state.master?.allSupportSubCategories || []);
    const { subCategoryId } = resolveSupportIds(ticket, masterCategories, allSubCategories);
    return (
        <MasterLabel
            type="masterSupportSubCategory"
            id={subCategoryId}
            field="name"
            fallback={ticket.subject || ''}
        />
    );
};

const MAX_SEARCH_LENGTH = 50;
const MAX_MESSAGE_LENGTH = 1000;
const UNASSIGNED = 'UNASSIGNED';
// Sentinel for the "No tenant" filter row (tickets whose tenantId is null).
const NO_TENANT = '__none__';
// Raiser kinds that can appear in the tenant queue — the filter chips.
const TENANT_RAISER_KINDS = ['doctor', 'tenant_admin', 'staff', 'tenant_patient'];

// Which side of the conversation a message belongs to. `senderSide` is what the
// server now stamps; the role compare is only for rows written before it existed.
const AGENT_ROLES = ['admin', 'super_admin', 'platform_staff'];
const sideOf = (msg) => {
    if (msg?.senderSide) return msg.senderSide;
    if (msg?.senderRole === 'system') return 'system';
    return AGENT_ROLES.includes(msg?.senderRole) ? 'support' : 'raiser';
};

// ---- Support SLA priority + live countdown ------------------------------
// Hours allotted to each frozen tier. 'Immediate' (Dedicated) gets the tightest
// window AND always ranks as the top group. Unknown tier → 48h (lowest).
const TIER_HOURS = { Immediate: 1, '12h': 12, '24h': 24, '48h': 48 };
const TIER_LABEL = { Immediate: 'Dedicated', '12h': '12h', '24h': '24h', '48h': '48h' };
const tierRank = (tier) => (tier === 'Immediate' ? 0 : 1);

// Time budget + remaining for a ticket relative to `now` (ms epoch).
const slaState = (ticket, now) => {
    const tier = ticket?.supportTier || '48h';
    const hours = TIER_HOURS[tier] ?? 48;
    const created = new Date(ticket?.createdAt || ticket?.created_at || now).getTime();
    const totalMs = hours * 3600 * 1000;
    const deadline = created + totalMs;
    const msLeft = deadline - now;
    return { tier, hours, totalMs, deadline, msLeft, overdue: msLeft < 0 };
};

// Priority: Dedicated group first, then least time-left first (overdue on top).
const sortByPriority = (list, now) =>
    [...(list || [])].sort((a, b) => {
        const sa = slaState(a, now), sb = slaState(b, now);
        const ra = tierRank(sa.tier), rb = tierRank(sb.tier);
        if (ra !== rb) return ra - rb;
        return sa.msLeft - sb.msLeft;
    });

// Compact largest-unit duration: "2d", "5h", "23m", "45s".
const fmtCompact = (ms) => {
    const s = Math.floor(Math.abs(ms) / 1000);
    if (s >= 86400) return `${Math.floor(s / 86400)}d`;
    if (s >= 3600) return `${Math.floor(s / 3600)}h`;
    if (s >= 60) return `${Math.floor(s / 60)}m`;
    return `${s}s`;
};
// Verbose duration for tooltips: "5h 02m", "2d 4h".
const fmtDur = (ms) => {
    const s = Math.floor(Math.abs(ms) / 1000);
    const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60);
    if (d > 0) return `${d}d ${h}h`;
    if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
    return `${m}m`;
};

// Circular SLA countdown ring. Self-ticking every 1s so it counts down (and up,
// once overdue) live without re-rendering the parent. Green → amber → red by
// fraction of time left; full red + "Overdue" past the deadline.
const SlaRing = ({ ticket, size = 46, stroke = 4 }) => {
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        const i = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(i);
    }, []);
    const { tier, totalMs, msLeft, overdue } = slaState(ticket, now);
    const fracLeft = overdue ? 0 : Math.max(0, Math.min(1, msLeft / totalMs));
    const color = overdue ? '#ef4444' : fracLeft > 0.5 ? '#10b981' : fracLeft > 0.2 ? '#f59e0b' : '#ef4444';
    const r = (size - stroke) / 2;
    const circ = 2 * Math.PI * r;
    const offset = circ * (1 - (overdue ? 1 : fracLeft));
    const label = TIER_LABEL[tier] || tier;
    return (
        <div
            data-testid="sla-ring"
            className="flex flex-col items-center justify-center flex-shrink-0"
            title={`${label} SLA · ${overdue ? `overdue by ${fmtDur(msLeft)}` : `${fmtDur(msLeft)} left`}`}
        >
            <div className="relative" style={{ width: size, height: size }}>
                <svg width={size} height={size} className="-rotate-90">
                    <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e5e7eb" strokeWidth={stroke} />
                    <circle
                        cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke}
                        strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
                        style={{ transition: 'stroke-dashoffset 0.6s linear, stroke 0.6s linear' }}
                    />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-[10px] font-extrabold leading-none" style={{ color }}>{fmtCompact(msLeft)}</span>
                </div>
            </div>
            <span className="mt-0.5 text-[9px] font-semibold leading-none" style={{ color: overdue ? '#ef4444' : '#6b7280' }}>
                {overdue ? 'Overdue' : 'left'}
            </span>
        </div>
    );
};

// ---- List / message helpers (pure) --------------------------------------
const removeFrom = (list, ticketId) => (list || []).filter((tk) => tk.ticketId !== ticketId);

// Put `ticket` at the front of `list`, keeping any messages the list copy already
// holds — a summary from the server never carries the conversation.
const upsertFront = (list, ticket) => {
    const existing = (list || []).find((tk) => tk.ticketId === ticket.ticketId);
    const merged = existing ? { ...existing, ...ticket, messages: existing.messages || ticket.messages } : ticket;
    return [merged, ...removeFrom(list, ticket.ticketId)];
};

// A fresh list from the server replaces the old one, but the messages already
// loaded onto list copies survive: lists refresh on every assignment/closure,
// and dropping the history each time would make the open chat go blank.
const mergeList = (prevList, nextList) => {
    const byId = new Map((prevList || []).map((tk) => [tk.ticketId, tk]));
    return (nextList || []).map((tk) => {
        const prev = byId.get(tk.ticketId);
        return prev?.messages ? { ...tk, messages: prev.messages } : tk;
    });
};

const sortByTime = (list) =>
    [...(list || [])].sort((a, b) => {
        const timeA = new Date(a.createdAt || a.time || 0).getTime();
        const timeB = new Date(b.createdAt || b.time || 0).getTime();
        return timeA - timeB;
    });

// Add a server message to a thread: skip a duplicate by messageId, and let it
// replace the optimistic `temp_` row the sender drew for the same text.
const appendMessage = (messages, incoming) => {
    const list = messages || [];
    if (list.some((m) => m.messageId === incoming.messageId)) return list;
    const filtered = list.filter(
        (m) => !(m.messageId?.startsWith('temp_') && m.message === incoming.message && sideOf(m) === sideOf(incoming))
    );
    return sortByTime([...filtered, incoming]);
};

// Normalise a message to one shape whichever path it came by (history, live
// event, optimistic row). `replyToMessage` is the quoted row when the server
// expanded it; older rows had the object under `replyTo` itself.
const normalizeMessage = (msg) => ({
    id: msg.id ?? msg._id ?? null,
    _id: msg._id ?? msg.id ?? null,
    messageId: msg.messageId || (msg.id != null ? `msg_${msg.id}` : `msg_${Date.now()}`),
    ticketId: msg.ticketId,
    senderId: msg.senderId ?? null,
    senderRole: msg.senderRole,
    senderSide: sideOf(msg),
    senderName: msg.senderName || null,
    message: msg.message,
    time: msg.createdAt || new Date().toISOString(),
    createdAt: msg.createdAt || new Date().toISOString(),
    isRead: msg.isRead || false,
    attachments: msg.attachments || [],
    replyTo: msg.replyTo ?? null,
    replyToMessage: msg.replyToMessage || (msg.replyTo && typeof msg.replyTo === 'object' ? msg.replyTo : null),
    // System event payload (assignment / reassignment banners).
    systemEvent: msg.systemEvent || null,
});

const SupportQueue = ({ queue = 'tenant' }) => {
    const { t } = useTranslation(['support']);
    const location = useLocation();
    const dispatch = useDispatch();
    const isTenantQueue = queue === 'tenant';
    // The business id (adminId / platformStaffId) the server writes into
    // `assignedTo`, so "is this mine" compares like with like.
    const myId = localStorage.getItem('ID');
    // 'admin' (the super admin, normalised at login) or 'platform_staff'.
    const myRole = localStorage.getItem('Role');

    const { agents, agentsLoading, agentsError } = useSelector((state) => state.supportQueue);
    const tenantOptions = useSelector((state) => state.tenant?.tenantOptions || []);

    const [activeTab, setActiveTab] = useState('unassigned');
    // Mirrors activeTab for socket handlers, which close over the first render.
    const activeTabRef = useRef('unassigned');
    useEffect(() => { activeTabRef.current = activeTab; }, [activeTab]);

    const [selectedTicket, setSelectedTicket] = useState(null);
    // Mirrors selectedTicket for the socket recovery handlers (re-auth,
    // reconnect), which close over the first render.
    const selectedTicketRef = useRef(null);
    useEffect(() => { selectedTicketRef.current = selectedTicket; }, [selectedTicket]);
    const [messageInput, setMessageInput] = useState('');
    const [messageError, setMessageError] = useState('');
    const [tickets, setTickets] = useState({
        unassignedTickets: [],
        myTickets: [],
        allOpenTickets: [],
        closedTickets: []
    });
    // SLA sub-tab filter (Unassigned / My Tickets / All Open) — by the ticket's frozen tier.
    const [tierFilter, setTierFilter] = useState('all');
    const SLA_TABS = [
        { id: 'all', label: 'All' },
        { id: 'Immediate', label: 'Dedicated' },
        { id: '12h', label: '12h' },
        { id: '24h', label: '24h' },
        { id: '48h', label: '48h' },
    ];
    // Re-sort the SLA lists periodically so priority reflects elapsed time (rings
    // self-tick every second; 30s is plenty for re-ordering the cards).
    const [, setSlaTick] = useState(0);
    useEffect(() => {
        const i = setInterval(() => setSlaTick((n) => n + 1), 30000);
        return () => clearInterval(i);
    }, []);
    const [isLoading, setIsLoading] = useState(true);
    const messagesEndRef = useRef(null);

    // Text search across the lists + the filter popover (tenant / raiser kind on
    // the tenant queue). The popover edits a DRAFT and commits on Apply, the
    // same way every other list screen in this app does it.
    const [search, setSearch] = useState('');
    const [filters, setFilters] = useState({ tenantId: '', raiserKinds: [] });
    const [tempFilters, setTempFilters] = useState({ tenantId: '', raiserKinds: [] });
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const filterWrapRef = useRef(null);
    const filtersActive = Boolean(filters.tenantId) || filters.raiserKinds.length > 0;

    // Pre-load master categories + flat subcategory list once on mount so the
    // TicketCategoryLine / TicketSubjectLabel helpers can fall back to name-based
    // lookups when the ticket payload doesn't include categoryId/subCategoryId.
    // Tenant names feed the filter's searchable list (tenant queue only).
    useEffect(() => {
        dispatch(getCategories());
        dispatch(getAllSupportSubCategories());
        if (isTenantQueue) dispatch(getTenantOptions());
    }, [dispatch, isTenantQueue]);

    const fileInputRef = useRef(null);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [previewImage, setPreviewImage] = useState(null);
    const [replyToMessage, setReplyToMessage] = useState(null);
    const [showScrollBottom, setShowScrollBottom] = useState(false);

    // Agent select dropdown state (per unassigned ticket card)
    const [openAdminDropdownId, setOpenAdminDropdownId] = useState(null);
    const [selectedAdmins, setSelectedAdmins] = useState({}); // { ticketId: agent }
    const [adminSearchQuery, setAdminSearchQuery] = useState('');
    const [adminSearchError, setAdminSearchError] = useState('');
    const adminDropdownRef = useRef(null);
    const adminSearchInputRef = useRef(null);

    // Reassign dropdown state (chat header)
    const [reassignDropdownOpen, setReassignDropdownOpen] = useState(false);
    const [reassignSearchQuery, setReassignSearchQuery] = useState('');
    const [reassignSearchError, setReassignSearchError] = useState('');
    const reassignDropdownRef = useRef(null);

    const chatAreaRef = useRef(null);
    const loadingTimeoutRef = useRef(null);
    const listenersAttached = useRef(false);
    const setupInProgress = useRef(false);
    const socketRetryCount = useRef(0);
    const refreshTimerRef = useRef(null);
    // Our own `connect` listener on the shared socket (rejoins the open ticket).
    const onReconnectRejoin = useRef(null);
    const MAX_SOCKET_RETRIES = 20; // Maximum 20 retries (2 seconds total)

    const handleScroll = () => {
        if (!chatAreaRef.current) return;
        const { scrollTop, scrollHeight, clientHeight } = chatAreaRef.current;
        const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
        setShowScrollBottom(!isNearBottom);
    };

    // Connect socket when the queue page is opened, disconnect when leaving
    useEffect(() => {
        console.log('🔌 Support queue opened - connecting socket...', queue);

        // Clear any pending disconnect (StrictMode remount, or the other queue's
        // instance unmounting a moment ago).
        if (pendingDisconnect) {
            clearTimeout(pendingDisconnect);
            pendingDisconnect = null;
        }

        if (!myId || !myRole) {
            console.error('❌ Agent identity not found');
            return undefined;
        }

        try {
            const socketInstance = connectSupportSocket({ role: myRole, id: myId });
            if (!socketInstance) {
                console.error('❌ Failed to create socket instance. Check authentication and configuration.');
                toast.warning(t('support.failed_initialize_support'));
            }
        } catch (error) {
            console.error('❌ Error connecting socket:', error);
            toast.warning(t('support.failed_connect_support'));
        }

        // Cleanup: disconnect when leaving the page — delayed, so a remount can
        // cancel it and keep the connection.
        return () => {
            pendingDisconnect = setTimeout(() => {
                console.log('✅ Disconnecting support socket (queue page unmounted)');
                disconnectSupportSocket();
                pendingDisconnect = null;
            }, 500);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Helper function to normalize ticket data from backend
    const normalizeTicket = (ticket) => ({
        ...ticket,
        category: ticket.category || ticket.categoryName || ticket.categoryId,
        subCategory: ticket.subCategory || ticket.subCategoryName || ticket.subCategoryId,
        userName: ticket.userName || ticket.userRole || t('support.unknown_user')
    });

    // Setup socket event listeners (socket connection is managed above)
    useEffect(() => {
        if (!myId || !myRole) {
            toast.warning(t('support.admin_id_not_found'));
            return undefined;
        }

        // Reset loading state when component mounts
        setIsLoading(true);

        const LIST_EVENTS = [
            'unassignedTickets', 'adminTickets', 'allOpenTickets', 'closedTickets',
            'newSupportTicket', 'adminTicketListUpdate', 'ticketAccepted', 'ticketAlreadyAssigned',
            'supportTicketJoined', 'supportHistory', 'receiveSupportMessage', 'supportTicketClosed',
            'ticketReassignmentSuccess', 'ticketAssignedSuccess', 'ticketReassigned', 'ticketAssigned',
            'supportError',
        ];
        // Always clean up old listeners before setting up new ones
        LIST_EVENTS.forEach((evt) => adminSocketEvents.removeListener(evt));

        const requestLists = () => adminSocketEvents.requestTickets({ queue });

        // Several list updates can arrive in one burst (assign + system message
        // + lastMessage). One refresh a beat later is enough to get the server's
        // truth for all four lists.
        const scheduleRefresh = () => {
            if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
            refreshTimerRef.current = setTimeout(() => {
                refreshTimerRef.current = null;
                if (isSupportSocketConnected()) requestLists();
            }, 400);
        };

        const setupListenersAndRequestTickets = () => {
            // Prevent duplicate setup
            if (listenersAttached.current) return;

            // Wait for socket instance to exist (even if not connected yet)
            let socket = getSupportSocket();

            // If socket doesn't exist, try to create it
            if (!socket) {
                try {
                    socket = connectSupportSocket({ role: myRole, id: myId });
                } catch (error) {
                    console.error('❌ Error creating socket:', error);
                }
                if (!socket) {
                    socketRetryCount.current += 1;
                    if (socketRetryCount.current >= MAX_SOCKET_RETRIES) {
                        console.error('❌ Socket instance not created after maximum retries. Please refresh the page.');
                        toast.warning(t('support.failed_to_initialize', 'Failed to initialize support service. Please refresh the page.'));
                        setIsLoading(false);
                        return;
                    }
                    setTimeout(setupListenersAndRequestTickets, 100); // Retry after 100ms
                    return;
                }
            }

            // Reset retry count on success
            socketRetryCount.current = 0;
            setupInProgress.current = true;
            listenersAttached.current = true;

            try {
                // ---- The four lists of this queue ----------------------------
                adminSocketEvents.onUnassignedTickets((data) => {
                    const normalized = Array.isArray(data) ? data.map(normalizeTicket) : [];
                    setTickets((prev) => ({ ...prev, unassignedTickets: mergeList(prev.unassignedTickets, normalized) }));
                    setIsLoading(false);
                });

                // "My Tickets" is whatever the server says is assigned to me.
                adminSocketEvents.onAdminTickets((data) => {
                    const normalized = Array.isArray(data) ? data.map(normalizeTicket) : [];
                    setTickets((prev) => ({ ...prev, myTickets: mergeList(prev.myTickets, normalized) }));
                    setIsLoading(false);
                });

                adminSocketEvents.onAllOpenTickets((data) => {
                    const normalized = Array.isArray(data) ? data.map(normalizeTicket) : [];
                    setTickets((prev) => ({ ...prev, allOpenTickets: mergeList(prev.allOpenTickets, normalized) }));
                    setIsLoading(false);
                });

                adminSocketEvents.onClosedTickets((data) => {
                    const normalized = Array.isArray(data) ? data.map(normalizeTicket) : [];
                    setTickets((prev) => ({ ...prev, closedTickets: mergeList(prev.closedTickets, normalized) }));
                    setIsLoading(false);
                });

                // The lists only arrive in answer to requestAdminTickets { queue }.
                const requestTicketsIfNeeded = () => {
                    if (!isSupportSocketConnected()) {
                        const s = getSupportSocket();
                        if (s) s.once('connect', () => requestLists());
                        return;
                    }
                    // Clear selected ticket when navigating back to force refresh
                    setSelectedTicket(null);
                    requestLists();
                };

                // Request immediately if socket already connected, otherwise wait for connect
                if (isSupportSocketConnected()) {
                    // Small delay to ensure listeners are fully attached
                    setTimeout(requestTicketsIfNeeded, 300);
                } else {
                    const s = getSupportSocket();
                    if (s) s.once('connect', () => setTimeout(requestTicketsIfNeeded, 300));
                }

                // Retry request after 3 seconds if nothing arrived
                setTimeout(() => {
                    setTickets((prev) => {
                        const hasData = prev.unassignedTickets.length > 0 || prev.myTickets.length > 0
                            || prev.allOpenTickets.length > 0 || prev.closedTickets.length > 0;
                        if (!hasData && isSupportSocketConnected()) requestLists();
                        return prev;
                    });
                }, 3000);

                // Fallback: stop loading after timeout — an empty queue is a
                // legitimate state, not a spinner forever.
                loadingTimeoutRef.current = setTimeout(() => setIsLoading(false), 3000);

                // If the socket had to re-authenticate (token expired while the tab
                // sat open), ask again — otherwise it recovers silently and the
                // screen stays empty, which looks exactly like having no tickets.
                // The open ticket's room did not survive either (re-auth leaves
                // every ticket_* room), so join it back or its live messages stop.
                const rejoinOpenTicket = () => {
                    const open = selectedTicketRef.current;
                    if (open?.ticketId) adminSocketEvents.joinTicket(open.ticketId);
                };
                setOnReauthenticated(() => {
                    requestLists();
                    rejoinOpenTicket();
                });
                // Same on a socket.io RECONNECT: the server's fresh socket is in
                // no room. The service re-requests the queue lists itself; the
                // open ticket is the page's to rejoin. A specific handler so
                // cleanup removes only ours, not the service's own connect one.
                // (off() with no handler would strip EVERY connect listener.)
                if (onReconnectRejoin.current) socket.off('connect', onReconnectRejoin.current);
                onReconnectRejoin.current = rejoinOpenTicket;
                socket.on('connect', onReconnectRejoin.current);

                // ---- Live queue events ------------------------------------------
                // A brand-new ticket in this queue. It may already be auto-assigned
                // (server picks the least-loaded active agent), so it goes to
                // whichever lists its assignee puts it in — plus All Open always.
                adminSocketEvents.onNewSupportTicket((data) => {
                    if (!data?.ticketId) return;
                    if (data.queue && data.queue !== queue) return;
                    const newTicket = normalizeTicket(data);
                    const assignee = newTicket.assignedTo;
                    const isUnassigned = !assignee || assignee === UNASSIGNED;
                    setTickets((prev) => ({
                        ...prev,
                        unassignedTickets: isUnassigned ? upsertFront(prev.unassignedTickets, newTicket) : prev.unassignedTickets,
                        myTickets: assignee === myId ? upsertFront(prev.myTickets, newTicket) : prev.myTickets,
                        allOpenTickets: upsertFront(prev.allOpenTickets, newTicket),
                    }));
                    scheduleRefresh();
                });

                // Any change to a ticket of this queue: assignment, reassignment,
                // closure, a new message. The payload is the ticket summary, so the
                // lists are patched from its status/assignee right away and the
                // server's lists are re-requested a beat later.
                adminSocketEvents.onAdminTicketListUpdate((data) => {
                    if (!data?.ticketId) return;
                    if (data.queue && data.queue !== queue) return;
                    const id = data.ticketId;
                    const summary = normalizeTicket(data);
                    const assignee = summary.assignedTo;
                    const isClosed = summary.status === 'closed';
                    const isUnassigned = !assignee || assignee === UNASSIGNED;

                    if (summary.status) {
                        setTickets((prev) => {
                            if (isClosed) {
                                return {
                                    unassignedTickets: removeFrom(prev.unassignedTickets, id),
                                    myTickets: removeFrom(prev.myTickets, id),
                                    allOpenTickets: removeFrom(prev.allOpenTickets, id),
                                    closedTickets: upsertFront(prev.closedTickets, summary),
                                };
                            }
                            return {
                                unassignedTickets: isUnassigned ? upsertFront(prev.unassignedTickets, summary) : removeFrom(prev.unassignedTickets, id),
                                myTickets: assignee === myId ? upsertFront(prev.myTickets, summary) : removeFrom(prev.myTickets, id),
                                allOpenTickets: upsertFront(prev.allOpenTickets, summary),
                                closedTickets: removeFrom(prev.closedTickets, id),
                            };
                        });
                        setSelectedTicket((prev) => {
                            if (prev?.ticketId !== id) return prev;
                            if (isClosed) return { ...prev, status: 'closed', closedAt: summary.closedAt || prev.closedAt };
                            // Reassigned away while I was reading it on My Tickets:
                            // it is no longer in that list, so drop the selection.
                            if (activeTabRef.current === 'myTickets' && assignee !== myId) return null;
                            return { ...prev, assignedTo: assignee, adminName: summary.adminName, autoAssigned: summary.autoAssigned };
                        });
                    }
                    scheduleRefresh();
                });

                // Listen for ticket accepted (answered to the caller only)
                adminSocketEvents.onTicketAccepted((data) => {
                    const raw = data?.ticket || data;
                    if (!raw?.ticketId) return;
                    const normalizedTicket = normalizeTicket(raw);
                    const assignedId = data?.adminId || normalizedTicket.assignedTo;
                    const isAssignedToMe = !assignedId || assignedId === myId;
                    const accepted = { ...normalizedTicket, assignedTo: assignedId || myId };

                    setTickets((prev) => ({
                        ...prev,
                        unassignedTickets: removeFrom(prev.unassignedTickets, accepted.ticketId),
                        allOpenTickets: upsertFront(prev.allOpenTickets, accepted),
                        myTickets: isAssignedToMe ? upsertFront(prev.myTickets, accepted) : prev.myTickets,
                    }));

                    if (isAssignedToMe) {
                        // Switch to My Tickets and open it; join the room so the
                        // raiser's replies arrive.
                        setActiveTab('myTickets');
                        setSelectedTicket({ ...accepted, messages: [] });
                        adminSocketEvents.joinTicket(accepted.ticketId);
                        toast.success(t('support.ticket_accepted_success'));
                    }
                });

                // Somebody else got there first
                adminSocketEvents.onTicketAlreadyAssigned((data) => {
                    if (!data?.ticketId) return;
                    setTickets((prev) => ({
                        ...prev,
                        unassignedTickets: removeFrom(prev.unassignedTickets, data.ticketId)
                    }));
                    toast.info(t('support.ticket_already_assigned'));
                    scheduleRefresh();
                });

                // Listen for ticket joined — the server sends a ticket summary with
                // lastMessage, not the full messages array
                adminSocketEvents.onTicketJoined((summary) => {
                    if (!summary?.ticketId) return;
                    const normalizedSummary = normalizeTicket(summary);
                    setSelectedTicket((prev) => {
                        const merged = {
                            ...normalizedSummary,
                            ...prev,
                            ticketId: normalizedSummary.ticketId || prev?.ticketId,
                            messages: prev?.messages || []
                        };
                        // Keep the status we already show; take the summary's otherwise.
                        if (prev?.status) merged.status = prev.status;
                        return merged;
                    });
                    // Fetch full message history
                    adminSocketEvents.fetchHistory(summary.ticketId, 1);
                });

                // Listen for message history
                adminSocketEvents.onHistory((data) => {
                    if (!data?.ticketId || !Array.isArray(data.messages)) return;
                    const sortedMessages = sortByTime(data.messages.map(normalizeMessage));

                    setSelectedTicket((prev) =>
                        prev?.ticketId === data.ticketId ? { ...prev, messages: sortedMessages } : prev
                    );

                    // Also keep the history on the list copy, so re-selecting the
                    // ticket shows it at once while the fresh history loads.
                    setTickets((prev) => {
                        const patchList = (list) => list.map((tk) => (tk.ticketId === data.ticketId ? { ...tk, messages: sortedMessages } : tk));
                        return {
                            unassignedTickets: patchList(prev.unassignedTickets),
                            myTickets: patchList(prev.myTickets),
                            allOpenTickets: patchList(prev.allOpenTickets),
                            closedTickets: patchList(prev.closedTickets),
                        };
                    });
                });

                // Listen for new messages (ticket room)
                adminSocketEvents.onReceiveMessage((data) => {
                    if (!data || !data.ticketId) return;
                    const normalizedMessage = normalizeMessage(data);

                    // Lists first (even if the ticket is not selected)
                    setTickets((prev) => {
                        const patchList = (list) => list.map((tk) => (
                            tk.ticketId === data.ticketId ? { ...tk, messages: appendMessage(tk.messages, normalizedMessage) } : tk
                        ));
                        return {
                            unassignedTickets: patchList(prev.unassignedTickets),
                            myTickets: patchList(prev.myTickets),
                            allOpenTickets: patchList(prev.allOpenTickets),
                            closedTickets: patchList(prev.closedTickets),
                        };
                    });

                    // Then the open conversation, on its OWN messages — never on a
                    // list copy, which may hold less history than the chat does.
                    setSelectedTicket((prev) => (
                        prev?.ticketId === data.ticketId
                            ? { ...prev, messages: appendMessage(prev.messages, normalizedMessage) }
                            : prev
                    ));
                });

                // Listen for ticket closed (ticket room)
                adminSocketEvents.onTicketClosed((data) => {
                    if (!data?.ticketId) return;
                    setTickets((prev) => {
                        const closedTicket = prev.myTickets.find((tk) => tk.ticketId === data.ticketId)
                            || prev.allOpenTickets.find((tk) => tk.ticketId === data.ticketId)
                            || prev.unassignedTickets.find((tk) => tk.ticketId === data.ticketId);
                        return {
                            unassignedTickets: removeFrom(prev.unassignedTickets, data.ticketId),
                            myTickets: removeFrom(prev.myTickets, data.ticketId),
                            allOpenTickets: removeFrom(prev.allOpenTickets, data.ticketId),
                            closedTickets: closedTicket
                                ? upsertFront(prev.closedTickets, { ...closedTicket, status: 'closed', closedAt: data.closedAt })
                                : prev.closedTickets
                        };
                    });

                    // Update selected ticket status and navigate to closed tab
                    setSelectedTicket((prev) => {
                        if (prev?.ticketId === data.ticketId) {
                            setActiveTab('closed');
                            return { ...prev, status: 'closed', closedAt: data.closedAt };
                        }
                        return prev;
                    });
                    scheduleRefresh();
                });

                // Reassignment I initiated succeeded
                adminSocketEvents.onTicketReassignmentSuccess((data) => {
                    const reassignedTicketId = data?.ticket?.ticketId || data?.ticketId;
                    if (!reassignedTicketId) return;
                    const newAssignee = data?.newAdminId || data?.ticket?.assignedTo;
                    toast.success(t('support.ticket_reassigned_success', 'Ticket reassigned successfully.'));
                    if (newAssignee === myId) {
                        // Took it over: it is mine now.
                        scheduleRefresh();
                        return;
                    }
                    setTickets((prev) => ({
                        ...prev,
                        myTickets: removeFrom(prev.myTickets, reassignedTicketId)
                    }));
                    setSelectedTicket((prev) => (
                        prev?.ticketId === reassignedTicketId && activeTabRef.current === 'myTickets' ? null : prev
                    ));
                    scheduleRefresh();
                });

                // Assignment I initiated (to someone else) succeeded
                adminSocketEvents.onTicketAssignedSuccess(() => {
                    toast.success(t('support.ticket_assigned_success', { defaultValue: 'Ticket assigned successfully' }));
                    scheduleRefresh();
                });

                // Reassigned (ticket room broadcast)
                adminSocketEvents.onTicketReassigned((data) => {
                    if (!data?.ticketId) return;
                    if (data.newAdminId === myId) {
                        // Fetch fresh lists to get the full ticket object into My Tickets
                        scheduleRefresh();
                    }
                    if (data.oldAdminId === myId && data.newAdminId !== myId) {
                        setTickets((prev) => ({
                            ...prev,
                            myTickets: removeFrom(prev.myTickets, data.ticketId)
                        }));
                        setSelectedTicket((prev) => (
                            prev?.ticketId === data.ticketId && activeTabRef.current === 'myTickets' ? null : prev
                        ));
                    }
                    setSelectedTicket((prev) => (
                        prev?.ticketId === data.ticketId
                            ? { ...prev, assignedTo: data.newAdminId ?? prev.assignedTo, adminName: data.newAdminName ?? data.adminName ?? prev.adminName }
                            : prev
                    ));
                });

                // Assigned (ticket room broadcast) — keep the header's assignee current
                adminSocketEvents.onTicketAssigned((data) => {
                    if (!data?.ticketId) return;
                    setSelectedTicket((prev) => (
                        prev?.ticketId === data.ticketId
                            ? { ...prev, assignedTo: data.adminId ?? data.assignedTo ?? prev.assignedTo, adminName: data.adminName ?? prev.adminName }
                            : prev
                    ));
                });

                // Any refused agent event: say what the server said.
                adminSocketEvents.onSupportError((err) => {
                    console.warn('⚠️ supportError:', err);
                    toast.warning(err?.message || t('support.failed_setup_listeners'));
                    setIsLoading(false);
                });
            } catch (error) {
                console.error('❌ Error setting up listeners:', error);
                toast.warning(t('support.failed_setup_listeners'));
                setIsLoading(false);
                setupInProgress.current = false;
                listenersAttached.current = false;
            }
        };

        setupListenersAndRequestTickets();

        // Cleanup on unmount
        return () => {
            if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
            if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
            setOnReauthenticated(null);
            if (onReconnectRejoin.current) {
                const s = getSupportSocket();
                if (s) s.off('connect', onReconnectRejoin.current);
                onReconnectRejoin.current = null;
            }

            // Reset flags for next mount
            setupInProgress.current = false;
            listenersAttached.current = false;
            socketRetryCount.current = 0;

            LIST_EVENTS.forEach((evt) => adminSocketEvents.removeListener(evt));
        };
        // Re-run when navigating to this page or switching queue.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.key, queue]);

    // Listen for FCM push notifications to refresh ticket lists
    useEffect(() => {
        const handleSupportUpdate = () => {
            if (isSupportSocketConnected()) adminSocketEvents.requestTickets({ queue });
        };
        window.addEventListener('support-ticket-update', handleSupportUpdate);
        return () => window.removeEventListener('support-ticket-update', handleSupportUpdate);
    }, [queue]);

    // Handle browser tab visibility changes
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState !== 'visible') return;

            // Check if socket is still connected
            if (!isSupportSocketConnected() && myId && myRole) {
                try {
                    connectSupportSocket({ role: myRole, id: myId });
                } catch (error) {
                    console.error('❌ Error reconnecting socket:', error);
                }
            }

            // Request fresh ticket data
            adminSocketEvents.requestTickets({ queue });

            // If a ticket is selected, refresh its data by rejoining
            if (selectedTicket?.ticketId) {
                adminSocketEvents.joinTicket(selectedTicket.ticketId);
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [selectedTicket, queue, myId, myRole]);

    // Close the filter popover on an outside click
    useEffect(() => {
        if (!isFilterOpen) return undefined;
        const handleClickOutside = (e) => {
            if (filterWrapRef.current && !filterWrapRef.current.contains(e.target)) setIsFilterOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isFilterOpen]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [selectedTicket?.messages]);

    // Sync selectedTicket with tickets list when messages are updated
    useEffect(() => {
        if (!selectedTicket?.ticketId) return;
        const ticketFromList = tickets.myTickets.find((tk) => tk.ticketId === selectedTicket.ticketId)
            || tickets.allOpenTickets.find((tk) => tk.ticketId === selectedTicket.ticketId)
            || tickets.unassignedTickets.find((tk) => tk.ticketId === selectedTicket.ticketId)
            || tickets.closedTickets.find((tk) => tk.ticketId === selectedTicket.ticketId);

        if (ticketFromList?.messages) {
            const listMessageCount = ticketFromList.messages.length;
            const selectedMessageCount = selectedTicket.messages?.length || 0;
            // If list has more messages, sync them
            if (listMessageCount > selectedMessageCount) {
                setSelectedTicket((prev) => ({ ...prev, messages: ticketFromList.messages }));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tickets.myTickets, tickets.unassignedTickets, tickets.allOpenTickets, tickets.closedTickets]);

    // Clear selected ticket if myTickets becomes empty
    useEffect(() => {
        if (activeTab === 'myTickets' && tickets.myTickets.length === 0 && selectedTicket) {
            setSelectedTicket(null);
        }
    }, [tickets.myTickets.length, activeTab, selectedTicket]);

    // ---- Filtering + priority ----------------------------------------------
    const matchesFilters = useCallback((tk) => {
        if (isTenantQueue) {
            if (filters.tenantId === NO_TENANT && tk.tenantId) return false;
            if (filters.tenantId && filters.tenantId !== NO_TENANT && tk.tenantId !== filters.tenantId) return false;
            if (filters.raiserKinds.length > 0 && !filters.raiserKinds.includes(tk.raiserKind)) return false;
        }
        const q = search.trim().toLowerCase();
        if (q) {
            const hay = [tk.ticketId, tk.userName, tk.userId, tk.subject, tk.tenantName, tk.categoryName, tk.subCategoryName, tk.adminName]
                .filter(Boolean).map((v) => String(v).toLowerCase());
            if (!hay.some((v) => v.includes(q))) return false;
        }
        return true;
    }, [isTenantQueue, filters, search]);

    const visible = useCallback((list) => (list || []).filter(matchesFilters), [matchesFilters]);
    const byTier = (list) => (tierFilter === 'all' ? (list || []) : (list || []).filter((tk) => (tk.supportTier || '48h') === tierFilter));
    // Filtered, tier-filtered AND priority-sorted: Dedicated group first, then
    // closest to its own SLA breach (overdue on top).
    const prioritized = (list) => sortByPriority(byTier(visible(list)), Date.now());

    const listForTab = (tab) => {
        if (tab === 'unassigned') return tickets.unassignedTickets;
        if (tab === 'myTickets') return tickets.myTickets;
        if (tab === 'allOpen') return tickets.allOpenTickets;
        return tickets.closedTickets;
    };

    // The agents this queue may be assigned to, minus me (the Accept button /
    // "Take over" already cover self), filtered by the dropdown's search.
    const selectableAgents = useMemo(() => {
        const q = (openAdminDropdownId ? adminSearchQuery : reassignSearchQuery).trim().toLowerCase();
        return (agents || [])
            .filter((a) => a.id !== myId)
            .filter((a) => !q || [a.name, a.email, a.roleName, a.id].filter(Boolean).some((v) => String(v).toLowerCase().includes(q)));
    }, [agents, myId, openAdminDropdownId, adminSearchQuery, reassignSearchQuery]);

    const loadAgents = () => {
        dispatch(fetchSupportAgents({ queue })).unwrap().catch(() => {
            toast.warning(t('support.agents_load_failed', 'Could not load the support agents.'));
        });
    };

    // ---- Files -----------------------------------------------------------------
    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        if (files.length + selectedFiles.length > 3) {
            toast.warning(t('chat.max_files_limit', 'Maximum 3 files allowed'));
            return;
        }

        const validFiles = files.filter((file) => {
            const isLt10M = file.size / 1024 / 1024 < 10;
            if (!isLt10M) toast.warning(t('chat.file_too_large', 'File size must be less than 10MB'));
            return isLt10M;
        });

        setSelectedFiles((prev) => [...prev, ...validFiles]);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const removeFile = (index) => {
        setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    };

    // Authenticated download. The endpoint requires a token, so this cannot be a
    // window.open(): a plain browser navigation carries no Authorization header.
    // Attachment URLs may be presigned (and expire), so only the stored object
    // key — the last path segment, query stripped — goes to the re-signing route.
    const handleDownload = async (fileUrl) => {
        if (!fileUrl) return;
        const fileName = String(fileUrl).split('?')[0].split('/').pop();
        try {
            const response = await fetch(`${baseURL}/support/message/download/${encodeURIComponent(fileName)}`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
            });
            if (!response.ok) throw new Error('Download failed');
            const url = window.URL.createObjectURL(await response.blob());
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (err) {
            console.error('Support attachment download failed:', err);
            toast.warning(t('support.download_failed', 'Could not download the attachment.'));
        }
    };

    // ---- Formatting ------------------------------------------------------------
    // A message's time of day is the one clock format, "2:30 PM" (utils/clock.js);
    // '' when there is no usable date, as before.
    const formatTime = (dateString) => formatClockTime(dateString);

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return t('support.date_just_now');
        if (diffMins < 60) return t('support.date_minutes_ago', { minutes: diffMins, plural: diffMins > 1 ? 's' : '' });
        if (diffHours < 24) return t('support.date_hours_ago', { hours: diffHours, plural: diffHours > 1 ? 's' : '' });
        if (diffDays < 7) return t('support.date_days_ago', { days: diffDays, plural: diffDays > 1 ? 's' : '' });
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    // Who raised it, in words: "Doctor", "Tenant admin", "MOC patient"…
    const raiserLabel = (ticket) => (
        ticket?.raiserKind
            ? t(`support.raiser_kind.${ticket.raiserKind}`, ticket.raiserKind)
            : (ticket?.userRole || '')
    );

    const isUnassignedTicket = (ticket) => !ticket?.assignedTo || ticket.assignedTo === UNASSIGNED;
    const isMine = (ticket) => ticket?.assignedTo === myId;

    // ---- Agent dropdown (unassigned cards) --------------------------------------
    const sanitizeSearch = (value) => value.replace(/[<>]/g, '');

    const handleAdminSearchChange = (e) => {
        const value = e.target.value;
        if (adminSearchError && value.length < adminSearchQuery.length) setAdminSearchError('');
        const sanitized = sanitizeSearch(value);
        if (sanitized.length > MAX_SEARCH_LENGTH) {
            setAdminSearchError(t('support.search_too_long', { length: MAX_SEARCH_LENGTH, defaultValue: `Search term cannot exceed ${MAX_SEARCH_LENGTH} characters` }));
        } else {
            setAdminSearchQuery(sanitized);
            setAdminSearchError('');
        }
    };

    const toggleAdminDropdown = (ticketId) => {
        if (openAdminDropdownId === ticketId) {
            setOpenAdminDropdownId(null);
        } else {
            setOpenAdminDropdownId(ticketId);
            loadAgents();
        }
        setAdminSearchQuery('');
        setAdminSearchError('');
    };

    // Warn (in the same dialog) when this isn't the top-priority unassigned
    // ticket — a higher-priority one is waiting.
    const priorityWarningHtml = (ticket) => {
        const ordered = prioritized(tickets.unassignedTickets);
        const topTicket = ordered[0];
        const isTop = !topTicket || topTicket.ticketId === ticket.ticketId;
        if (isTop) return '';
        const st = slaState(topTicket, Date.now());
        const topLabel = TIER_LABEL[st.tier] || st.tier;
        const topLeft = st.overdue ? `overdue by ${fmtDur(st.msLeft)}` : `${fmtDur(st.msLeft)} left`;
        return `<div style="margin-bottom:10px;padding:10px 12px;background:#fff7ed;border:1px solid #fed7aa;border-radius:10px;color:#b45309;font-size:13px;text-align:left;line-height:1.5;">`
            + `⚠️ <b>${topTicket.ticketId}</b> (${topLabel} · ${topLeft}) is the current <b>top priority</b>. `
            + `You're selecting a lower-priority ticket <b>${ticket.ticketId}</b>.</div>`;
    };

    // Confirm, then accept for myself or assign to the chosen agent. The server
    // takes the assigner from the token; only the ticket and the target travel.
    const confirmAssign = (ticket, agent) => {
        const isSelf = !agent || agent.id === myId;
        const assignName = agent ? `${agent.name || ''}`.trim() || t('support.raiser_kind.staff', 'Staff') : '';
        const priorityHtml = priorityWarningHtml(ticket);
        const isTop = !priorityHtml;

        const confirmText = isSelf
            ? t('support.confirm_accept_text', { defaultValue: 'Are you sure you want to accept this ticket?' })
            : t('support.confirm_assign_text', { name: assignName, defaultValue: `Are you sure you want to assign this ticket to ${assignName}?` });
        const confirmBtnText = isSelf
            ? t('support.yes_accept', { defaultValue: 'Yes, accept it!' })
            : t('support.yes_assign', { defaultValue: 'Yes, assign it!' });

        Swal.fire({
            title: !isTop
                ? t('support.confirm_skip_priority_title', { defaultValue: 'Skip higher priority?' })
                : isSelf
                    ? t('support.confirm_accept_title', { defaultValue: 'Accept Ticket' })
                    : t('support.confirm_assign_title', { defaultValue: 'Assign Ticket' }),
            html: `${priorityHtml}<div>${confirmText}</div>`,
            icon: !isTop ? 'warning' : 'question',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#d33',
            confirmButtonText: !isTop
                ? t('support.yes_continue_anyway', { defaultValue: 'Continue anyway' })
                : confirmBtnText,
            cancelButtonText: t('support.cancel', { defaultValue: 'Cancel' })
        }).then((result) => {
            if (result.isConfirmed) {
                if (isSelf) adminSocketEvents.acceptTicket(ticket.ticketId);
                else adminSocketEvents.assignTicket(ticket.ticketId, agent.id);
            }
            // Clear selection regardless of confirm/cancel
            setSelectedAdmins((prev) => {
                const updated = { ...prev };
                delete updated[ticket.ticketId];
                return updated;
            });
        });
    };

    // Select an agent from the dropdown — immediately confirm assignment to them
    const handleSelectAdmin = (ticketId, agent) => {
        setSelectedAdmins((prev) => ({ ...prev, [ticketId]: agent }));
        setOpenAdminDropdownId(null);
        setAdminSearchQuery('');
        setAdminSearchError('');
        const ticket = tickets.unassignedTickets.find((tk) => tk.ticketId === ticketId);
        if (ticket) confirmAssign(ticket, agent);
    };

    // Accept button — self, or the agent picked in the card's dropdown
    const handleAcceptTicket = (ticket) => {
        const picked = selectedAdmins[ticket.ticketId];
        confirmAssign(ticket, picked && picked.id !== myId ? picked : null);
    };

    // Click-outside to close the card dropdown
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (adminDropdownRef.current && !adminDropdownRef.current.contains(e.target)) {
                setOpenAdminDropdownId(null);
                setAdminSearchQuery('');
                setAdminSearchError('');
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // ---- Reassign dropdown (chat header) ------------------------------------------
    const handleReassignSearchChange = (e) => {
        const value = e.target.value;
        if (reassignSearchError && value.length < reassignSearchQuery.length) setReassignSearchError('');
        const sanitized = sanitizeSearch(value);
        if (sanitized.length > MAX_SEARCH_LENGTH) {
            setReassignSearchError(t('support.search_too_long', { length: MAX_SEARCH_LENGTH, defaultValue: `Search term cannot exceed ${MAX_SEARCH_LENGTH} characters` }));
        } else {
            setReassignSearchQuery(sanitized);
            setReassignSearchError('');
        }
    };

    const toggleReassignDropdown = () => {
        if (!reassignDropdownOpen) loadAgents();
        setReassignDropdownOpen((v) => !v);
        setReassignSearchQuery('');
        setReassignSearchError('');
    };

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (reassignDropdownRef.current && !reassignDropdownRef.current.contains(e.target)) {
                setReassignDropdownOpen(false);
                setReassignSearchQuery('');
                setReassignSearchError('');
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleReassignAdmin = (agent) => {
        setReassignDropdownOpen(false);
        setReassignSearchQuery('');
        setReassignSearchError('');
        if (!selectedTicket || !agent || agent.id === myId) return;

        const newAdminName = `${agent.name || ''}`.trim() || t('support.raiser_kind.staff', 'Staff');
        Swal.fire({
            title: t('support.confirm_reassign_title', { defaultValue: 'Reassign Ticket' }),
            text: t('support.confirm_reassign_text', { name: newAdminName, defaultValue: `Are you sure you want to reassign this ticket to ${newAdminName}?` }),
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#d33',
            confirmButtonText: t('support.yes_reassign', { defaultValue: 'Yes, reassign it!' }),
            cancelButtonText: t('support.cancel', { defaultValue: 'Cancel' })
        }).then((result) => {
            if (result.isConfirmed) adminSocketEvents.reassignTicket(selectedTicket.ticketId, agent.id);
        });
    };

    // "Take over" — from All Open, pull a ticket another agent holds onto my desk.
    const handleTakeOver = () => {
        if (!selectedTicket || isMine(selectedTicket)) return;
        Swal.fire({
            title: t('support.confirm_take_over_title', 'Take over this ticket?'),
            text: t('support.confirm_take_over_text', { name: selectedTicket.adminName || t('support.another_admin', 'another admin'), defaultValue: `It is currently handled by ${selectedTicket.adminName || 'another agent'}. It will move to your tickets.` }),
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#d33',
            confirmButtonText: t('support.yes_take_over', 'Yes, take it over'),
            cancelButtonText: t('support.cancel', { defaultValue: 'Cancel' })
        }).then((result) => {
            if (result.isConfirmed) adminSocketEvents.reassignTicket(selectedTicket.ticketId, myId);
        });
    };

    const handleSelectTicket = (ticket) => {
        // Always take the latest copy from the lists so loaded messages come along
        const latestTicket = tickets.myTickets.find((tk) => tk.ticketId === ticket.ticketId)
            || tickets.allOpenTickets.find((tk) => tk.ticketId === ticket.ticketId)
            || tickets.unassignedTickets.find((tk) => tk.ticketId === ticket.ticketId)
            || tickets.closedTickets.find((tk) => tk.ticketId === ticket.ticketId)
            || ticket;

        setSelectedTicket(latestTicket);
        setReplyToMessage(null);
        // Close reassign dropdown when switching tickets
        setReassignDropdownOpen(false);
        setReassignSearchQuery('');
        setReassignSearchError('');

        // Join the ticket room to receive messages (participant check server-side)
        adminSocketEvents.joinTicket(latestTicket.ticketId);
    };

    const scrollToMessage = (msgId) => {
        if (!msgId) return;
        const element = document.getElementById(`message-${msgId}`);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            element.classList.add('bg-[#4ABDBD]/10', 'ring-[#4ABDBD]/50');
            setTimeout(() => {
                element.classList.remove('bg-[#4ABDBD]/10', 'ring-[#4ABDBD]/50');
            }, 2000);
        }
    };

    // ---- Composer -----------------------------------------------------------------
    const validateMessage = (value) => {
        const sanitized = value.replace(/[<>]/g, '');
        if (sanitized.length > MAX_MESSAGE_LENGTH) return t('support.message_too_long', { length: MAX_MESSAGE_LENGTH });
        if (value.length > 0 && sanitized.trim() === '') return t('support.message_spaces_only');
        return '';
    };

    const handleMessageChange = (e) => {
        let sanitized = e.target.value.replace(/[<>]/g, '');
        if (sanitized.length > MAX_MESSAGE_LENGTH) sanitized = sanitized.substring(0, MAX_MESSAGE_LENGTH);
        setMessageInput(sanitized);
        setMessageError(validateMessage(sanitized));
    };

    // The id the server accepts as replyTo: the quoted row's own message id.
    const replyTargetId = (msg) => msg?.id ?? msg?._id ?? null;

    // Multipart send — the same route the raiser apps use. No senderId /
    // senderRole fields: the server derives the sender from the bearer token.
    const postMultipart = async (fields, files) => {
        const formData = new FormData();
        formData.append('ticketId', selectedTicket.ticketId);
        Object.entries(fields).forEach(([k, v]) => { if (v != null && v !== '') formData.append(k, v); });
        files.forEach((file) => formData.append('files', file));
        await axios.post(`${baseURL}/support/message/send`, formData, {
            headers: {
                Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
                'Content-Type': 'multipart/form-data',
            },
        });
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();

        const trimmedMessage = messageInput.trim();
        if (trimmedMessage) {
            const error = validateMessage(trimmedMessage);
            if (error) {
                setMessageError(error);
                return;
            }
        }

        if ((!trimmedMessage && selectedFiles.length === 0) || !selectedTicket || isUploading) return;

        if (!isSupportSocketConnected()) {
            toast.warning(t('support.not_connected_server'));
            return;
        }

        const currentFiles = [...selectedFiles];
        const replyTo = replyTargetId(replyToMessage);

        if (currentFiles.length > 0) {
            setIsUploading(true);
            try {
                await postMultipart({ message: trimmedMessage, replyTo }, currentFiles);
                setMessageInput('');
                setSelectedFiles([]);
                setMessageError('');
                setReplyToMessage(null);
            } catch (error) {
                console.error('Error sending support message with files:', error.response?.data || error.message);
                toast.warning(t('support.send_failed', 'Failed to send message'));
            } finally {
                setIsUploading(false);
            }
            return;
        }

        const messageData = { ticketId: selectedTicket.ticketId, message: trimmedMessage };
        if (replyTo) messageData.replyTo = replyTo;
        adminSocketEvents.sendMessage(messageData);

        // Optimistic UI update (replaced by the server's row when it arrives)
        const newMessage = normalizeMessage({
            messageId: `temp_${Date.now()}`,
            ticketId: selectedTicket.ticketId,
            senderId: myId,
            senderRole: myRole,
            senderSide: 'support',
            message: trimmedMessage,
            createdAt: new Date().toISOString(),
            replyToMessage: replyToMessage || null,
        });
        setReplyToMessage(null);

        setSelectedTicket((prev) => (prev ? { ...prev, messages: sortByTime([...(prev.messages || []), newMessage]) } : prev));
        setTickets((prev) => {
            const patchList = (list) => list.map((tk) => (
                tk.ticketId === selectedTicket.ticketId ? { ...tk, messages: [...(tk.messages || []), newMessage] } : tk
            ));
            return { ...prev, myTickets: patchList(prev.myTickets), allOpenTickets: patchList(prev.allOpenTickets) };
        });

        setMessageInput('');
        setMessageError('');
    };

    const handleVoiceNote = (file) => {
        if (!selectedTicket) return;
        setIsUploading(true);
        postMultipart({}, [file])
            .catch((err) => {
                console.error('Error sending voice note:', err);
                toast.warning(t('support.send_failed'));
            })
            .finally(() => setIsUploading(false));
    };

    const handleCloseTicket = async () => {
        if (!selectedTicket) return;

        const result = await Swal.fire({
            title: t('support.close_ticket_confirm'),
            text: t('support.close_ticket_text', 'Are you sure you want to close this ticket? This action cannot be undone.'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#6B7280',
            confirmButtonText: t('support.yes_close', 'Yes, close it!'),
            cancelButtonText: t('support.cancel', 'Cancel'),
            reverseButtons: true
        });

        if (result.isConfirmed) {
            adminSocketEvents.closeTicket(selectedTicket.ticketId);
            // Immediately update selected ticket status for instant UI feedback
            setSelectedTicket((prev) => (prev ? { ...prev, status: 'closed' } : null));
            setActiveTab('closed');
        }
    };

    const getTabCount = (tab) => visible(listForTab(tab)).length;

    const getStatusBadge = (status) => {
        const configs = {
            open: { label: t('support.status_open'), color: 'bg-blue-100 text-blue-800', icon: Clock },
            assigned: { label: t('support.status_assigned'), color: 'bg-yellow-100 text-yellow-800', icon: Clock },
            closed: { label: t('support.status_closed'), color: 'bg-gray-100 text-gray-800', icon: CheckCircle2 }
        };
        const config = configs[status?.toLowerCase()] || configs.open;
        const Icon = config.icon;
        return (
            <span data-testid={`support-queue-status-badge-${status?.toLowerCase() || 'open'}`} className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
                <Icon size={12} />
                {config.label}
            </span>
        );
    };

    // ---- Small chips -------------------------------------------------------------
    const RaiserChip = ({ ticket, testId }) => (
        <span data-testid={testId} className="px-2 py-0.5 bg-gray-100 rounded-full text-xs">
            {raiserLabel(ticket)}
        </span>
    );
    const TenantChip = ({ ticket, testId }) => (
        isTenantQueue ? (
            <span data-testid={testId} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#4ABDBD]/10 text-[#2D8A8A] text-[11px] font-semibold max-w-[220px]">
                <Building2 size={11} className="flex-shrink-0" />
                <span className="truncate">{ticket.tenantName || (ticket.tenantId ? t('moc.hr_detail_hospital', 'Hospital') : t('support.no_tenant', 'No tenant'))}</span>
            </span>
        ) : null
    );
    const AutoChip = ({ ticket, testId }) => (
        ticket.autoAssigned ? (
            <span data-testid={testId} title={t('support.auto_assigned', 'Auto-assigned')} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#D7A05C]/15 text-[#8a5a1a] text-[11px] font-semibold">
                <Zap size={11} />
                {t('support.auto_assigned', 'Auto-assigned')}
            </span>
        ) : null
    );
    // WHO HOLDS IT — the chip lives in ./AssigneeChip (owner, 5 Oct 2026).
    const AssigneeLine = ({ ticket, testId }) => {
        const unassigned = isUnassignedTicket(ticket);
        return (
            <AssigneeChip
                unassigned={unassigned}
                mine={!unassigned && isMine(ticket)}
                name={ticket.adminName}
                testId={testId}
            />
        );
    };

    // The searchable agent list shared by the card dropdown and the reassign menu.
    const renderAgentList = (onPick, testPrefix) => (
        <ul data-testid={`${testPrefix}-agent-list`} className="max-h-60 overflow-y-auto custom-scrollbar">
            {agentsError && (
                <li className="px-4 py-2 text-xs text-red-600">{agentsError}</li>
            )}
            {selectableAgents.length === 0 ? (
                <li data-testid={`${testPrefix}-no-agents`} className="px-4 py-3 text-gray-500 text-center text-sm">
                    {(openAdminDropdownId ? adminSearchQuery : reassignSearchQuery)
                        ? t('support.no_admins_found', { defaultValue: 'No agents found' })
                        : t('support.no_admins_available', { defaultValue: 'No agents available' })}
                </li>
            ) : (
                selectableAgents.map((agent, idx) => (
                    <li
                        data-testid={`${testPrefix}-agent-option-${agent.id}`}
                        key={agent.id}
                        onClick={() => onPick(agent)}
                        className={`px-4 py-2.5 transition flex justify-between items-center gap-2 cursor-pointer hover:bg-[#4ABDBD] hover:text-white text-sm group/agent
                            ${idx !== selectableAgents.length - 1 ? 'border-b border-gray-100' : ''}`}
                    >
                        <span className="min-w-0">
                            <span className="block truncate font-medium">{agent.name || t('support.raiser_kind.staff', 'Staff')}</span>
                            <span className="block truncate text-[11px] opacity-70">
                                {agent.isSuperAdmin ? t('support.role_super_admin', 'Super Admin') : (agent.roleName || '')}
                            </span>
                        </span>
                        <span
                            title={t('support.open_count', { count: agent.openCount ?? 0, defaultValue: `${agent.openCount ?? 0} open` })}
                            className="flex-shrink-0 px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 text-[10px] font-semibold group-hover/agent:bg-white/20 group-hover/agent:text-white"
                        >
                            {agent.openCount ?? 0}
                        </span>
                    </li>
                ))
            )}
        </ul>
    );

    const renderAgentSearch = (value, onChange, error, testPrefix, inputRef) => (
        <div className="p-2 border-b border-gray-200">
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                <input
                    data-testid={`${testPrefix}-agent-search-input`}
                    ref={inputRef}
                    type="text"
                    value={value}
                    onChange={onChange}
                    maxLength={50}
                    placeholder={t('support.search_admin', { defaultValue: 'Search by name, email, role...' })}
                    className={`w-full pl-9 pr-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 ${error
                        ? 'border-red-300 bg-red-50 focus:ring-red-500'
                        : 'border-gray-300 focus:ring-[#4ABDBD]'
                        }`}
                    autoFocus
                />
            </div>
            {error && <p data-testid={`${testPrefix}-agent-search-error`} className="text-xs text-red-600 mt-1 px-1">{error}</p>}
        </div>
    );

    // A row of the My Tickets / All Open / Closed list.
    const renderListItem = (ticket, { showSla, showAssignee, closed }) => (
        <button
            data-testid={`support-queue-ticket-item-${ticket.ticketId}`}
            key={ticket.ticketId}
            onClick={() => handleSelectTicket(ticket)}
            className={`w-full p-4 text-left hover:bg-gray-100 transition-colors ${selectedTicket?.ticketId === ticket.ticketId
                ? 'bg-[#4ABDBD]/10 border-l-4 border-[#4ABDBD]'
                : 'bg-white'
                }`}
        >
            <div className="flex items-start justify-between mb-2">
                <span className="font-semibold text-gray-800 text-sm">{ticket.ticketId}</span>
                <div className="flex items-center gap-2">
                    {getStatusBadge(ticket.status)}
                    {showSla && <SlaRing ticket={ticket} size={34} stroke={3} />}
                </div>
            </div>
            <p className="text-xs text-gray-600 mb-1 flex flex-wrap items-center gap-1.5">
                <span>{ticket.userName || t('support.unknown_user')}</span>
                <RaiserChip ticket={ticket} />
            </p>
            {isTenantQueue && (
                <p className="mb-1"><TenantChip ticket={ticket} /></p>
            )}
            {ticket.subject && (
                <p className="text-xs text-gray-700 font-medium mb-1">
                    <TicketSubjectLabel ticket={ticket} />
                </p>
            )}
            {(ticket.category || ticket.subCategory || ticket.categoryId || ticket.subCategoryId) && (
                <p className="text-xs text-gray-500">
                    <TicketCategoryLine ticket={ticket} />
                </p>
            )}
            {showAssignee && (
                <p className="mt-1 flex flex-wrap items-center gap-2">
                    <AssigneeLine ticket={ticket} testId={`support-queue-ticket-assignee-${ticket.ticketId}`} />
                    <AutoChip ticket={ticket} />
                </p>
            )}
            <p className="text-xs text-gray-400 mt-2">
                {closed ? `${t('support.closed_prefix')} ${formatDate(ticket.closedAt)}` : formatDate(ticket.createdAt)}
            </p>
        </button>
    );

    // System banners (assignment / reassignment) — centered pill. Older rows
    // may carry event types this side no longer produces; they render as text.
    const renderSystemMessage = (message, index) => {
        const evt = message.systemEvent || {};
        const isAssignEvt = evt.type === 'assigned';
        const isReassignEvt = evt.type === 'reassigned';
        return (
            <div
                data-testid={`support-queue-system-message-${index}`}
                id={`message-${message.messageId}`}
                key={message.messageId || index}
                className="flex justify-center my-3"
            >
                <div className="max-w-[85%] px-4 py-2 rounded-full bg-gradient-to-r from-[#4ABDBD]/10 to-[#3AA8A8]/10 border border-[#4ABDBD]/30 text-center">
                    {isAssignEvt && (
                        <p className="text-xs text-gray-700">
                            <span className="font-semibold text-[#2D8A8A]">
                                {t('support.ticket_assigned_banner', { name: evt.toAdminName || '', defaultValue: 'Ticket assigned' })}
                            </span>
                            {evt.auto ? (
                                <>
                                    <span className="mx-1">·</span>
                                    <span className="font-semibold">{t('support.auto_assigned', 'Auto-assigned')}</span>
                                </>
                            ) : evt.assignedByAdminName ? (
                                <>
                                    <span className="mx-1">·</span>
                                    {t('support.assigned_by_prefix', { defaultValue: 'by' })}{' '}
                                    <span className="font-semibold">{evt.assignedByAdminName}</span>
                                </>
                            ) : null}
                        </p>
                    )}
                    {isReassignEvt && (
                        <p className="text-xs text-gray-700">
                            <span className="font-semibold text-[#2D8A8A]">
                                {t('support.ticket_reassigned_banner', { name: evt.toAdminName || '', defaultValue: 'Ticket reassigned' })}
                            </span>
                            <span className="mx-1">·</span>
                            {t('support.previously_handled_by', { defaultValue: 'Previously handled by' })}{' '}
                            <span className="font-semibold">{evt.fromAdminName || t('support.another_admin', 'another admin')}</span>
                            <span className="block text-[11px] text-gray-500 mt-0.5">
                                {t('support.continue_from_here', { defaultValue: 'Please continue from here.' })}
                            </span>
                        </p>
                    )}
                    {!isAssignEvt && !isReassignEvt && (
                        <p className="text-xs text-gray-600">{message.message || evt.type || ''}</p>
                    )}
                </div>
            </div>
        );
    };

    // One chat bubble. Support-side rows sit on the right; a colleague's row
    // (All Open, or after a hand-over) still shows who wrote it.
    const renderMessage = (message, index, { readOnly }) => {
        const side = sideOf(message);
        if (side === 'system') return renderSystemMessage(message, index);

        const isSupport = side === 'support';
        const isBot = message.senderRole === 'bot';
        const isOwn = isSupport && (!message.senderId || message.senderId === myId);

        let senderName = message.senderName;
        if (!senderName) {
            if (isBot) senderName = t('support.sender_support_bot', 'Support Bot');
            else if (isOwn) senderName = t('support.sender_you', 'You');
            else if (isSupport) senderName = t('support.sender_agent', 'Support');
            else senderName = selectedTicket?.userName || raiserLabel(selectedTicket) || t('support.sender_user', 'User');
        }
        const quoted = message.replyToMessage;
        const quotedIsSupport = quoted ? sideOf(quoted) === 'support' : false;

        return (
            <div
                data-testid={`support-queue-message-row-${index}`}
                id={`message-${message.messageId}`}
                key={message.messageId || index}
                className={`flex ${isSupport ? 'justify-end' : 'justify-start'} mb-3 group/msg py-1.5`}
            >
                <div className={`flex flex-col ${isSupport ? 'items-end' : 'items-start'} max-w-[70%] relative`}>
                    {/* Sender name — received messages, and colleagues' replies */}
                    {!isOwn && (
                        <span className="text-xs font-bold text-[#4ABDBD] mb-1 px-2">{senderName}</span>
                    )}

                    {/* Message Bubble */}
                    <div
                        data-testid={`support-queue-message-bubble-${index}`}
                        className={`rounded-2xl px-4 py-2 shadow-sm relative transition-all duration-300 ${isSupport
                            ? 'bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white rounded-br-md'
                            : isBot
                                ? 'bg-blue-50 text-blue-900 border border-blue-200 rounded-bl-md'
                                : 'bg-white text-gray-800 border border-gray-200 rounded-bl-md'
                            }`}
                    >
                        {/* Reply To Reference inside bubble */}
                        {quoted && typeof quoted === 'object' && (
                            <div
                                onClick={() => scrollToMessage(quoted.messageId || quoted._id || quoted.id)}
                                className={`mb-2 p-2 rounded-xl text-xs border-l-4 cursor-pointer hover:opacity-80 transition-opacity ${isSupport ? 'bg-white/10 border-white/40 text-white/90' : 'bg-gray-50 border-[#4ABDBD] text-gray-600'
                                    }`}>
                                <div className="font-semibold text-[10px] mb-0.5 opacity-80 flex items-center gap-1">
                                    <Reply size={10} />
                                    {quoted.senderName || (quotedIsSupport ? t('support.sender_agent', 'Support') : t('support.sender_user', 'User'))}
                                </div>
                                <div className="line-clamp-2 truncate">{quoted.message || t('support.attachment', 'Attachment')}</div>
                            </div>
                        )}
                        {message.message && (
                            <p className="text-sm whitespace-pre-wrap break-words leading-relaxed">{message.message}</p>
                        )}

                        {/* Attachments */}
                        {message.attachments && message.attachments.length > 0 && (
                            <div className="mt-3 grid gap-2 min-w-[200px]">
                                {message.attachments.map((file, i) => (
                                    <div key={i} className={`rounded-xl overflow-hidden border ${isSupport ? 'border-white/20 bg-white/10' : 'border-gray-100 bg-gray-50'}`}>
                                        {file.fileType === 'image' || (file.mimeType && file.mimeType.startsWith('image/')) ? (
                                            <div className="relative group cursor-pointer" onClick={() => setPreviewImage(getFileURL(file.url))}>
                                                <img
                                                    src={getFileURL(file.url)}
                                                    alt={file.fileName || t('support.image_alt', 'Image')}
                                                    className="max-w-full h-auto max-h-52 object-contain mx-auto"
                                                />
                                                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                                                    <button type="button" className="p-2.5 bg-white/20 backdrop-blur-md rounded-full text-white hover:bg-white/40 shadow-sm transition-all scale-90 group-hover:scale-100">
                                                        <Eye size={20} />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="p-2.5 bg-white/20 backdrop-blur-md rounded-full text-white hover:bg-white/40 shadow-sm transition-all scale-90 group-hover:scale-100"
                                                        onClick={(e) => { e.stopPropagation(); handleDownload(file.url); }}
                                                    >
                                                        <Download size={20} />
                                                    </button>
                                                </div>
                                            </div>
                                        ) : file.fileType === 'voice' || (file.mimeType && file.mimeType.startsWith('audio/')) ? (
                                            <div className={`p-3 min-w-[240px] ${isSupport ? 'bg-white/10' : 'bg-[#4ABDBD]/5'} rounded-xl`}>
                                                <div className="flex items-center gap-2 mb-2">
                                                    <Volume2 size={16} className={isSupport ? 'text-white' : 'text-[#4ABDBD]'} />
                                                    <span className={`text-xs font-bold ${isSupport ? 'text-white' : 'text-gray-700'}`}>
                                                        {t('chat.voice_message', 'Voice Message')}
                                                    </span>
                                                </div>
                                                <audio
                                                    src={getFileURL(file.url)}
                                                    controls
                                                    className={`w-[255px] h-12 custom-audio-player ${isSupport ? 'invert brightness-200' : ''}`}
                                                />
                                            </div>
                                        ) : (
                                            <div className="p-3 flex items-center gap-3">
                                                <div className={`p-2 rounded-lg ${isSupport ? 'bg-white/20' : 'bg-white shadow-sm'}`}>
                                                    <File size={20} />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-xs font-bold truncate">{file.fileName}</p>
                                                    <p className="text-[10px] opacity-60 uppercase">{file.mimeType ? file.mimeType.split('/')[1] : 'FILE'}</p>
                                                </div>
                                                <button
                                                    type="button"
                                                    className={`p-2 rounded-lg transition-colors ${isSupport ? 'hover:bg-white/20' : 'hover:bg-gray-200'}`}
                                                    onClick={() => handleDownload(file.url)}
                                                >
                                                    <Download size={18} />
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        <p className={`text-xs mt-1.5 ${isSupport ? 'text-white/70' : 'text-gray-500'}`}>
                            {formatTime(message.createdAt || message.time)}
                        </p>
                    </div>

                    {/* Reply Button (Hover) */}
                    {!readOnly && !isBot && (
                        <div className={`absolute top-1/2 -translate-y-1/2 opacity-0 group-hover/msg:opacity-100 transition-opacity flex items-center gap-1 z-10 ${isSupport ? 'right-[calc(100%+8px)]' : 'left-[calc(100%+8px)]'}`}>
                            <button
                                type="button"
                                data-testid={`support-queue-message-reply-button-${index}`}
                                onClick={() => setReplyToMessage(message)}
                                className="p-1.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-600 rounded-full shadow-sm transition-all"
                                title={t('support.reply', 'Reply')}
                            >
                                <Reply size={14} />
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // The chat pane's header: who / what / SLA / actions.
    const renderChatHeader = ({ readOnly }) => {
        const closed = selectedTicket.status === 'closed';
        const unassigned = isUnassignedTicket(selectedTicket);
        const mine = isMine(selectedTicket);
        return (
            <div data-testid="support-queue-chat-header" className="p-4 border-b border-gray-200 bg-gradient-to-r from-[#4ABDBD]/10 to-[#3AA8A8]/10">
                <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <h3 data-testid="support-queue-chat-ticket-id" className="font-semibold text-gray-800">{selectedTicket.ticketId}</h3>
                            {closed && (
                                <span className="px-2 py-0.5 bg-gray-200 text-gray-700 text-xs rounded-full font-medium">
                                    {t('support.ticket_closed')}
                                </span>
                            )}
                            <TenantChip ticket={selectedTicket} testId="support-queue-chat-tenant" />
                        </div>
                        <p data-testid="support-queue-chat-user-info" className="text-sm text-gray-600 flex flex-wrap items-center gap-1.5">
                            <span>{selectedTicket.userName || t('support.unknown_user')}</span>
                            <RaiserChip ticket={selectedTicket} />
                        </p>
                        {selectedTicket.subject && (
                            <p className="text-xs text-gray-700 mt-1 font-medium">
                                {t('support.subject_label')}{' '}
                                <TicketSubjectLabel ticket={selectedTicket} />
                            </p>
                        )}
                        {(selectedTicket.category || selectedTicket.subCategory || selectedTicket.categoryId || selectedTicket.subCategoryId) && (
                            <p className="text-xs text-gray-500 mt-0.5">
                                <TicketCategoryLine ticket={selectedTicket} />
                            </p>
                        )}
                        <p className="mt-1 flex flex-wrap items-center gap-2">
                            <AssigneeLine ticket={selectedTicket} testId="support-queue-chat-assignee" />
                            <AutoChip ticket={selectedTicket} />
                        </p>
                    </div>

                    {readOnly || closed ? (
                        getStatusBadge(selectedTicket.status)
                    ) : (
                        <div className="flex items-center gap-3 flex-wrap justify-end">
                            <SlaRing ticket={selectedTicket} size={52} stroke={5} />
                            {getStatusBadge(selectedTicket.status)}

                            {/* Not mine yet: accept it (unassigned) or take it over. */}
                            {unassigned ? (
                                <button
                                    data-testid="support-queue-chat-accept-button"
                                    onClick={() => confirmAssign(selectedTicket, null)}
                                    className="px-4 py-2 bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white rounded-lg hover:from-[#3AA8A8] hover:to-[#2A8A8A] transition-all font-medium text-sm whitespace-nowrap"
                                >
                                    {t('support.accept')}
                                </button>
                            ) : !mine ? (
                                <button
                                    data-testid="support-queue-take-over-button"
                                    onClick={handleTakeOver}
                                    className="px-3 py-2 border border-[#4ABDBD] text-[#2D8A8A] rounded-lg hover:bg-[#4ABDBD]/5 transition-all text-sm whitespace-nowrap font-semibold"
                                >
                                    {t('support.take_over', 'Take over')}
                                </button>
                            ) : null}

                            {/* Reassign dropdown */}
                            <div ref={reassignDropdownRef} className="relative w-52">
                                <button
                                    data-testid="support-queue-reassign-dropdown-button"
                                    onClick={toggleReassignDropdown}
                                    className="w-full px-3 py-2 border rounded-xl flex justify-between items-center font-medium transition text-sm text-gray-700 bg-white border-gray-300 cursor-pointer hover:border-[#4ABDBD]"
                                >
                                    <span className="truncate w-[85%] text-left">
                                        {t('support.reassign_to', { defaultValue: 'Reassign to...' })}
                                    </span>
                                    <span className="ml-1 flex items-center">
                                        {agentsLoading && reassignDropdownOpen ? (
                                            <span className="h-4 w-4 border-2 border-[#4ABDBD] border-t-transparent rounded-full animate-spin"></span>
                                        ) : reassignDropdownOpen ? (
                                            <ChevronUp size={16} />
                                        ) : (
                                            <ChevronDown size={16} />
                                        )}
                                    </span>
                                </button>

                                {reassignDropdownOpen && (
                                    <div data-testid="support-queue-reassign-dropdown" className="absolute right-0 mt-2 w-72 z-50 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
                                        {renderAgentSearch(reassignSearchQuery, handleReassignSearchChange, reassignSearchError, 'support-queue-reassign', null)}
                                        {agentsLoading ? (
                                            <div className="p-4 flex justify-center">
                                                <span className="h-5 w-5 border-2 border-[#4ABDBD] border-t-transparent rounded-full animate-spin"></span>
                                            </div>
                                        ) : renderAgentList(handleReassignAdmin, 'support-queue-reassign')}
                                    </div>
                                )}
                            </div>

                            <button
                                data-testid="support-queue-close-ticket-button"
                                onClick={handleCloseTicket}
                                className="px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-all text-sm whitespace-nowrap"
                            >
                                {t('support.close_ticket')}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // List + chat, shared by My Tickets, All Open and Closed.
    const renderListAndChat = ({ tab, list, heading, emptyText, showSla, showAssignee, closed }) => {
        const readOnly = closed;
        return (
            <div data-testid={`support-queue-${tab}-section`} className="flex-1 flex overflow-hidden">
                {/* Ticket List */}
                <div className="w-80 border-r border-gray-200 overflow-y-auto bg-gray-50 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:hover:bg-gray-400">
                    <div className="p-4 border-b border-gray-200 bg-white">
                        <h3 className="font-semibold text-gray-800">{heading} ({list.length})</h3>
                    </div>
                    {list.length === 0 ? (
                        <div className="p-4 text-center text-gray-500 text-sm">
                            <p>{emptyText}</p>
                        </div>
                    ) : (
                        <div className="divide-y divide-gray-200">
                            {list.map((ticket) => renderListItem(ticket, { showSla, showAssignee, closed }))}
                        </div>
                    )}
                </div>

                {/* Chat Area */}
                <div data-testid={`support-queue-${tab}-chat-area`} className="flex-1 flex flex-col bg-white">
                    {selectedTicket ? (
                        <>
                            {renderChatHeader({ readOnly })}

                            {/* Messages */}
                            <div className="flex-1 relative min-h-0 flex flex-col">
                                <div
                                    data-testid="support-queue-messages-container"
                                    ref={chatAreaRef}
                                    className="flex-1 overflow-y-auto p-4 space-y-2 bg-gray-50 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:hover:bg-gray-400"
                                    onScroll={handleScroll}
                                >
                                    {selectedTicket.messages?.map((message, index) => renderMessage(message, index, { readOnly: readOnly || selectedTicket.status === 'closed' }))}
                                    <div ref={messagesEndRef} />
                                </div>
                                <AnimatePresence>
                                    {showScrollBottom && (
                                        <MotionButton
                                            data-testid="support-queue-scroll-bottom-button"
                                            initial={{ opacity: 0, scale: 0.8, y: 10 }}
                                            animate={{ opacity: 1, scale: 1, y: 0 }}
                                            exit={{ opacity: 0, scale: 0.8, y: 10 }}
                                            onClick={() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })}
                                            className="absolute bottom-4 right-6 p-3 bg-[#4ABDBD] text-white rounded-full shadow-lg hover:bg-[#3A9B9B] transition-all z-10 flex items-center justify-center group"
                                            title={t('chat.recent_messages', 'Recent messages')}
                                        >
                                            <ChevronDown size={20} className="group-hover:translate-y-0.5 transition-transform" />
                                        </MotionButton>
                                    )}
                                </AnimatePresence>
                            </div>

                            {/* Input Area — never on a closed ticket */}
                            {!readOnly && selectedTicket.status !== 'closed' && (
                                <div data-testid="support-queue-input-area" className="p-4 border-t border-gray-200 bg-white relative">
                                    {/* Reply to preview in input box area */}
                                    {replyToMessage && (
                                        <div data-testid="support-queue-reply-preview" className="mb-3 px-4 py-3 bg-gray-50 rounded-xl border border-gray-200 flex items-start justify-between shadow-sm">
                                            <div className="flex-1 min-w-0 pr-4">
                                                <div className="flex items-center gap-1 text-xs font-semibold text-[#4ABDBD] mb-1">
                                                    <Reply size={12} />
                                                    {t('support.replying_to', 'Replying to')}{' '}
                                                    {sideOf(replyToMessage) === 'support' && (!replyToMessage.senderId || replyToMessage.senderId === myId)
                                                        ? t('support.sender_you', 'You')
                                                        : (replyToMessage.senderName || t('support.sender_user', 'User'))}
                                                </div>
                                                <p className="text-sm text-gray-600 truncate">{replyToMessage.message || t('support.attachment', 'Attachment')}</p>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => setReplyToMessage(null)}
                                                className="text-gray-400 hover:text-red-500 transition-colors p-1"
                                            >
                                                <X size={16} />
                                            </button>
                                        </div>
                                    )}

                                    {/* File Previews */}
                                    {selectedFiles.length > 0 && (
                                        <div data-testid="support-queue-file-previews" className="mb-3 flex flex-wrap gap-2">
                                            {selectedFiles.map((file, index) => (
                                                <div key={index} className="relative group bg-gray-50 border border-gray-200 rounded-xl p-2 pr-9 flex items-center gap-2 shadow-sm">
                                                    {file.type.startsWith('image/') ? (
                                                        <div className="w-8 h-8 rounded-lg bg-[#4ABDBD]/10 flex items-center justify-center overflow-hidden">
                                                            <ImageIcon size={16} className="text-[#4ABDBD]" />
                                                        </div>
                                                    ) : (
                                                        <div className="w-8 h-8 rounded-lg bg-gray-200 flex items-center justify-center">
                                                            <File size={16} className="text-gray-500" />
                                                        </div>
                                                    )}
                                                    <span className="text-[11px] font-bold text-gray-700 truncate max-w-[100px]">{file.name}</span>
                                                    <button
                                                        type="button"
                                                        onClick={() => removeFile(index)}
                                                        className="absolute right-1 text-gray-400 hover:text-red-500 p-1 transition-colors"
                                                    >
                                                        <X size={14} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    <form data-testid="support-queue-message-form" onSubmit={handleSendMessage} className="flex flex-col gap-2">
                                        <div className="flex gap-2 items-center">
                                            <input
                                                data-testid="support-queue-file-input"
                                                type="file"
                                                ref={fileInputRef}
                                                onChange={handleFileChange}
                                                multiple
                                                className="hidden"
                                                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                                            />
                                            <button
                                                data-testid="support-queue-attach-file-button"
                                                type="button"
                                                onClick={() => fileInputRef.current?.click()}
                                                className="p-2 text-gray-400 hover:text-[#4ABDBD] hover:bg-[#4ABDBD]/5 rounded-xl transition-all border border-gray-200"
                                                title={t('chat.attach_files', 'Attach Files')}
                                            >
                                                <Paperclip size={20} />
                                            </button>

                                            <input
                                                data-testid="support-queue-message-input"
                                                type="text"
                                                value={messageInput}
                                                onChange={handleMessageChange}
                                                maxLength={MAX_MESSAGE_LENGTH}
                                                disabled={isUploading}
                                                placeholder={t('support.type_message_placeholder')}
                                                className={`flex-1 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${messageError
                                                    ? 'border-red-300 bg-red-50 focus:ring-red-500'
                                                    : 'border-gray-300 focus:ring-[#4ABDBD]'
                                                    }`}
                                            />
                                            {messageInput.trim() || selectedFiles.length > 0 ? (
                                                <button
                                                    data-testid="support-queue-send-message-button"
                                                    type="submit"
                                                    disabled={isUploading}
                                                    className={`px-6 h-[42px] flex items-center justify-center bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white rounded-lg hover:from-[#3AA8A8] hover:to-[#2A8A8A] transition-all ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                >
                                                    {isUploading ? <Loader2 size={20} className="animate-spin" /> : <Send size={20} />}
                                                </button>
                                            ) : (
                                                <VoiceRecorder
                                                    isUploading={isUploading}
                                                    disabled={isUploading}
                                                    onRecordingComplete={handleVoiceNote}
                                                />
                                            )}
                                        </div>
                                        {messageError && (
                                            <p data-testid="support-queue-message-error" className="text-xs text-red-600">{messageError}</p>
                                        )}
                                    </form>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="flex-1 flex items-center justify-center text-gray-500">
                            <div className="text-center">
                                <MessageCircle size={48} className="mx-auto mb-4 text-gray-300" />
                                <p>{closed ? t('support.select_closed_ticket') : t('support.select_ticket_message')}</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const TABS = [
        { id: 'unassigned', label: t('support.unassigned_tickets_tab', 'Unassigned Tickets'), count: true },
        { id: 'myTickets', label: t('support.my_tickets'), count: true },
        { id: 'allOpen', label: t('support.all_open', 'All Open'), count: true },
        { id: 'closed', label: t('support.closed_tickets'), count: false },
    ];

    return (
        <>
            <div data-testid="support-queue-container" className="h-full flex flex-col bg-white rounded-2xl shadow-md overflow-hidden">
                {/* Header: title + search + filters */}
                <div data-testid="support-queue-header" className="px-6 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div>
                        <h1 data-testid="support-queue-title" className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent">
                            {t(`support.queue.${queue}`, queue === 'tenant' ? 'Tenant Support' : 'Patient Support')}
                        </h1>
                        <p className="text-xs text-gray-500 mt-1">
                            {t(`support.queue_sub.${queue}`, queue === 'tenant'
                                ? 'Tickets raised by hospital admins, doctors, staff and hospital patients.'
                                : 'Tickets raised from the Mitra One Care patient app.')}
                        </p>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                        <div className="relative w-full sm:w-72">
                            <input
                                data-testid="support-queue-search"
                                value={search}
                                onChange={(e) => setSearch(sanitizeSearch(e.target.value).slice(0, MAX_SEARCH_LENGTH))}
                                placeholder={t('support.search_tickets_ph', 'Search ticket / user / subject…')}
                                className="w-full p-2 pl-10 pr-8 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] text-sm"
                            />
                            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#4ABDBD]" />
                            {search && (
                                <button type="button" onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500 cursor-pointer">
                                    <X className="h-4 w-4" />
                                </button>
                            )}
                        </div>

                        {/* Tenant + raiser-kind filters — tenant queue only. The
                            patient queue has one raiser kind and no hospital. */}
                        {isTenantQueue && (
                            <div ref={filterWrapRef} className="relative flex items-center gap-2">
                                <button
                                    type="button"
                                    data-testid="support-queue-filter-button"
                                    onClick={() => { setTempFilters(filters); setIsFilterOpen((v) => !v); }}
                                    className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border text-sm transition-colors cursor-pointer ${filtersActive
                                        ? 'border-[#4ABDBD] text-[#2D8A8A] bg-[#4ABDBD]/5 font-semibold'
                                        : 'border-gray-300 text-gray-600 hover:border-[#4ABDBD]'
                                        }`}
                                >
                                    <Filter className="w-4 h-4 text-[#4ABDBD]" />
                                    {t('common.filter', 'Filter')}
                                </button>

                                {isFilterOpen && (
                                    <FilterPopover>
                                        <FilterPanel
                                            onClear={() => {
                                                setTempFilters({ tenantId: '', raiserKinds: [] });
                                                setFilters({ tenantId: '', raiserKinds: [] });
                                                setIsFilterOpen(false);
                                            }}
                                            onApply={() => { setFilters(tempFilters); setIsFilterOpen(false); }}
                                            clearLabel={t('common.clear', 'Clear')}
                                            applyLabel={t('common.apply', 'Apply')}
                                        >
                                            <FilterSection title={t('support.filter_tenant', 'Tenant')} isActive={!!tempFilters.tenantId}>
                                                <FilterSearchableList
                                                    items={[
                                                        { tenantId: NO_TENANT, name: t('support.no_tenant', 'No tenant') },
                                                        ...tenantOptions,
                                                    ]}
                                                    valueKey="tenantId"
                                                    labelKey="name"
                                                    sublabelKey="__none__"
                                                    searchKeys={['tenantId']}
                                                    value={tempFilters.tenantId
                                                        ? (tempFilters.tenantId === NO_TENANT
                                                            ? { tenantId: NO_TENANT, name: t('support.no_tenant', 'No tenant') }
                                                            : tenantOptions.find((x) => x.tenantId === tempFilters.tenantId) || null)
                                                        : null}
                                                    onChange={(item) => setTempFilters((f) => ({ ...f, tenantId: item?.tenantId || '' }))}
                                                    allLabel={t('support.all_tenants', 'All tenants')}
                                                    searchPlaceholder={t('common.search', 'Search…')}
                                                />
                                            </FilterSection>

                                            <FilterSection title={t('support.filter_raiser_kind', 'Raised by')} isActive={tempFilters.raiserKinds.length > 0}>
                                                <FilterOptionList
                                                    multiple
                                                    value={tempFilters.raiserKinds}
                                                    onChange={(v) => setTempFilters((f) => ({ ...f, raiserKinds: v }))}
                                                    options={TENANT_RAISER_KINDS.map((kind) => ({ value: kind, label: t(`support.raiser_kind.${kind}`, kind) }))}
                                                />
                                            </FilterSection>
                                        </FilterPanel>
                                    </FilterPopover>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Tabs */}
                <div data-testid="support-queue-tabs-container" className="border-b border-gray-200 bg-white">
                    <div className="flex gap-1 px-6 overflow-x-auto">
                        {TABS.map((tab) => (
                            <button
                                key={tab.id}
                                data-testid={`support-queue-tab-${tab.id}`}
                                onClick={() => {
                                    setActiveTab(tab.id);
                                    setSelectedTicket(null);
                                }}
                                className={`relative px-4 py-3 font-medium text-sm transition-colors whitespace-nowrap ${activeTab === tab.id
                                    ? 'text-[#4ABDBD] border-b-2 border-[#4ABDBD]'
                                    : 'text-gray-500 hover:text-gray-700'
                                    }`}
                            >
                                {tab.label}
                                {tab.count && getTabCount(tab.id) > 0 && (
                                    <span data-testid={`support-queue-tab-${tab.id}-count`} className="ml-2 px-2 py-0.5 bg-[#4ABDBD] text-white text-xs rounded-full">
                                        {getTabCount(tab.id)}
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>
                </div>

                {/* SLA tier sub-tabs (open lists) — high priority → low */}
                {activeTab !== 'closed' && (
                    <div data-testid="support-queue-tier-tabs" className="flex flex-wrap items-center gap-2 px-4 sm:px-6 py-2 border-b border-gray-100 bg-gray-50/60">
                        {SLA_TABS.map((tab) => {
                            const list = visible(listForTab(activeTab));
                            const count = tab.id === 'all' ? list.length : list.filter((tk) => (tk.supportTier || '48h') === tab.id).length;
                            const on = tierFilter === tab.id;
                            return (
                                <button
                                    key={tab.id}
                                    type="button"
                                    data-testid={`support-queue-tier-tab-${tab.id}`}
                                    onClick={() => setTierFilter(tab.id)}
                                    className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${on ? 'bg-[#4ABDBD] text-white shadow-sm' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-100'}`}
                                >
                                    {tab.label} <span className="opacity-70">({count})</span>
                                </button>
                            );
                        })}
                    </div>
                )}

                {/* Content Area */}
                <div data-testid="support-queue-content-area" className="flex-1 flex overflow-hidden">
                    {/* TAB 1: Unassigned Tickets */}
                    {activeTab === 'unassigned' && (
                        <div data-testid="support-queue-unassigned-section" className="flex-1 overflow-y-auto p-6 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:hover:bg-gray-400">
                            <div className="w-full">
                                <h2 className="text-lg font-semibold text-gray-800 mb-4">
                                    {t('support.unassigned_tickets')} ({prioritized(tickets.unassignedTickets).length})
                                </h2>
                                {isLoading ? (
                                    <div data-testid="support-queue-loading-container" className="flex items-center justify-center py-12">
                                        <Loader2 className="animate-spin text-[#4ABDBD]" size={32} />
                                        <span className="ml-3 text-gray-600">{t('support.loading_tickets')}</span>
                                    </div>
                                ) : prioritized(tickets.unassignedTickets).length === 0 ? (
                                    <div data-testid="support-queue-no-unassigned-container" className="text-center py-12 text-gray-500">
                                        <CheckCircle2 size={48} className="mx-auto mb-4 text-gray-300" />
                                        <p>{t('support.no_unassigned_tickets')}</p>
                                    </div>
                                ) : (
                                    <div data-testid="support-queue-unassigned-list" className="space-y-4">
                                        {prioritized(tickets.unassignedTickets).map((ticket) => (
                                            <div
                                                data-testid={`support-queue-unassigned-card-${ticket.ticketId}`}
                                                key={ticket.ticketId}
                                                className="p-4 border-2 border-gray-200 rounded-xl hover:border-[#4ABDBD] transition-all bg-white"
                                            >
                                                <div className="flex items-start justify-between mb-3 gap-3">
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                                                            <span className="font-bold text-gray-800">{ticket.ticketId}</span>
                                                            {getStatusBadge(ticket.status)}
                                                            <TenantChip ticket={ticket} testId={`support-queue-unassigned-tenant-${ticket.ticketId}`} />
                                                            <AutoChip ticket={ticket} />
                                                        </div>
                                                        <div className="flex items-center gap-3 text-sm text-gray-600 mb-2 flex-wrap">
                                                            <span className="flex items-center gap-1">
                                                                <User size={14} />
                                                                {ticket.userName || t('support.unknown_user')}
                                                            </span>
                                                            <RaiserChip ticket={ticket} testId={`support-queue-unassigned-raiser-${ticket.ticketId}`} />
                                                        </div>
                                                        <div className="flex items-center gap-4 text-sm text-gray-600 flex-wrap">
                                                            {ticket.subject && (
                                                                <span className="font-medium text-gray-700">
                                                                    <TicketSubjectLabel ticket={ticket} />
                                                                </span>
                                                            )}
                                                            {(ticket.category || ticket.subCategory || ticket.categoryId || ticket.subCategoryId) && (
                                                                <span className="flex items-center gap-1">
                                                                    <Tag size={14} />
                                                                    <TicketCategoryLine ticket={ticket} />
                                                                </span>
                                                            )}
                                                            <span className="flex items-center gap-1">
                                                                <Calendar size={14} />
                                                                {formatDate(ticket.createdAt)}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-2 ml-4">
                                                        <SlaRing ticket={ticket} />
                                                        {/* Agent Select Dropdown — delegate to a colleague */}
                                                        <div ref={openAdminDropdownId === ticket.ticketId ? adminDropdownRef : null} className="relative w-52">
                                                            <button
                                                                data-testid={`support-queue-agent-dropdown-button-${ticket.ticketId}`}
                                                                onClick={() => toggleAdminDropdown(ticket.ticketId)}
                                                                className={`w-full px-3 py-2 border rounded-xl flex justify-between items-center font-medium transition text-sm
                                                                    ${selectedAdmins[ticket.ticketId]
                                                                        ? 'text-white bg-gradient-to-br from-[#4ABDBD] to-[#3A9B9B] cursor-pointer'
                                                                        : 'text-gray-700 bg-white border-gray-300 cursor-pointer hover:border-[#4ABDBD]'
                                                                    }`}
                                                            >
                                                                <span className="truncate w-[85%] text-left">
                                                                    {selectedAdmins[ticket.ticketId]
                                                                        ? `${selectedAdmins[ticket.ticketId].name || t('support.raiser_kind.staff', 'Staff')}`
                                                                        : t('support.select_admin', { defaultValue: 'Select agent' })}
                                                                </span>
                                                                <span className="ml-1 flex items-center">
                                                                    {agentsLoading && openAdminDropdownId === ticket.ticketId ? (
                                                                        <span className="h-4 w-4 border-2 border-[#4ABDBD] border-t-transparent rounded-full animate-spin"></span>
                                                                    ) : openAdminDropdownId === ticket.ticketId ? (
                                                                        <ChevronUp size={16} />
                                                                    ) : (
                                                                        <ChevronDown size={16} />
                                                                    )}
                                                                </span>
                                                            </button>

                                                            {openAdminDropdownId === ticket.ticketId && (
                                                                <div data-testid={`support-queue-agent-dropdown-${ticket.ticketId}`} className="absolute left-0 mt-2 w-full z-50 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
                                                                    {renderAgentSearch(adminSearchQuery, handleAdminSearchChange, adminSearchError, 'support-queue-assign', adminSearchInputRef)}
                                                                    {agentsLoading ? (
                                                                        <div className="p-4 flex justify-center">
                                                                            <span className="h-5 w-5 border-2 border-[#4ABDBD] border-t-transparent rounded-full animate-spin"></span>
                                                                        </div>
                                                                    ) : renderAgentList((agent) => handleSelectAdmin(ticket.ticketId, agent), 'support-queue-assign')}
                                                                </div>
                                                            )}
                                                        </div>

                                                        {/* Accept Button */}
                                                        <button
                                                            data-testid={`support-queue-accept-button-${ticket.ticketId}`}
                                                            onClick={() => handleAcceptTicket(ticket)}
                                                            className="px-4 py-2 bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white rounded-lg hover:from-[#3AA8A8] hover:to-[#2A8A8A] transition-all font-medium text-sm whitespace-nowrap"
                                                        >
                                                            {t('support.accept')}
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* TAB 2: My Tickets */}
                    {activeTab === 'myTickets' && renderListAndChat({
                        tab: 'my-tickets',
                        list: prioritized(tickets.myTickets),
                        heading: t('support.my_tickets'),
                        emptyText: t('support.no_tickets_assigned'),
                        showSla: true,
                        showAssignee: false,
                        closed: false,
                    })}

                    {/* TAB 3: All Open — every open ticket of the queue, whoever holds it */}
                    {activeTab === 'allOpen' && renderListAndChat({
                        tab: 'all-open',
                        list: prioritized(tickets.allOpenTickets),
                        heading: t('support.all_open', 'All Open'),
                        emptyText: t('support.no_open_tickets', 'No open tickets.'),
                        showSla: true,
                        showAssignee: true,
                        closed: false,
                    })}

                    {/* TAB 4: Closed Tickets (read-only) */}
                    {activeTab === 'closed' && renderListAndChat({
                        tab: 'closed',
                        list: visible(tickets.closedTickets),
                        heading: t('support.closed_tickets'),
                        emptyText: t('support.no_closed_tickets', 'No closed tickets.'),
                        showSla: false,
                        showAssignee: true,
                        closed: true,
                    })}
                </div>
            </div>

            {/* Image Preview Modal */}
            <AnimatePresence>
                {previewImage && (
                    <MotionDiv
                        data-testid="support-queue-image-preview-modal"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
                        onClick={() => setPreviewImage(null)}
                    >
                        <MotionDiv
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            className="relative max-w-4xl w-full max-h-[90vh] flex items-center justify-center"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <button
                                type="button"
                                className="absolute -top-12 right-0 p-2 text-white/70 hover:text-white transition-colors"
                                onClick={() => setPreviewImage(null)}
                            >
                                <X size={24} />
                            </button>
                            <img
                                src={previewImage}
                                alt={t('chat.preview', 'Preview')}
                                className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
                            />
                            <div className="absolute -bottom-12 left-0 right-0 flex justify-center">
                                <button
                                    type="button"
                                    className="px-6 py-2 bg-white text-gray-900 rounded-full font-medium shadow-lg hover:bg-gray-100 transition-colors flex items-center gap-2"
                                    onClick={() => handleDownload(previewImage)}
                                >
                                    <Download size={18} />
                                    {t('chat.download', 'Download')}
                                </button>
                            </div>
                        </MotionDiv>
                    </MotionDiv>
                )}
            </AnimatePresence>
        </>
    );
};

export default SupportQueue;
